/**
 * The two languages the product ships in (contracts.md "Локализация
 * клиента"): every user-facing sentence is a method, so a missing
 * translation is a type error, not a fallback. The desktop's i18n.rs is the
 * pattern; this is its web twin with the strings F1–F3 need.
 *
 * Service-message wording lives here and not on the wire (D71): the server
 * sends the fact, the client writes the sentence in the reader's language.
 */
import type { TtlUnit } from "./ttl";

export type Lang = "ru" | "en";

export function detectLang(): Lang {
  const prefs = navigator.languages ?? [navigator.language];
  for (const p of prefs) {
    if (p.toLowerCase().startsWith("ru")) return "ru";
    if (p.toLowerCase().startsWith("en")) return "en";
  }
  return "en";
}

/** `m:ss` — the only countdown either language spells the same way. */
function clock(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds));
  return `${Math.floor(safe / 60)}:${String(safe % 60).padStart(2, "0")}`;
}

/** Two byte counts in the unit the second is said in — powers of 1024, one
 * place after the point, the point the language uses — and that unit's short
 * form: the pill over the composer (D398) says the unit once, and its first
 * number climbs towards the second instead of changing units halfway. The
 * desktop's `Lang::upload_amount`, digit for digit. */
function amountIn(lang: Lang, done: number, total: number): [string, string, string] {
  const units = lang === "ru" ? ["Б", "КБ", "МБ", "ГБ"] : ["B", "KB", "MB", "GB"];
  let unit = 0;
  let scale = 1;
  while (total / scale >= 1024 && unit < units.length - 1) {
    scale *= 1024;
    unit += 1;
  }
  const say = (n: number) => {
    if (unit === 0) return String(Math.floor(n));
    const text = (n / scale).toFixed(1);
    return lang === "ru" ? text.replace(".", ",") : text;
  };
  return [say(done), say(total), units[unit]!];
}

/** A post's view count beside its time (docs/broadcast-channels-plan.md D1083),
 * as the desktop's `Lang::views_short`: cut and not rounded, so it never says
 * more than has happened; a tenth only under ten, where it is still news. */
function viewsShort(n: number, point: string): string {
  if (n < 1000) return String(n);
  const [size, unit] = n < 1_000_000 ? [1000, "K"] : [1_000_000, "M"];
  const whole = Math.floor(n / size);
  const tenth = Math.floor(n / (size / 10)) % 10;
  return tenth === 0 || whole >= 10 ? `${whole}${unit}` : `${whole}${point}${tenth}${unit}`;
}

/** Russian needs three plural forms; English two. */
function plural(lang: Lang, n: number, one: string, few: string, many: string): string {
  if (lang === "en") return n === 1 ? one : many;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

/** Every language the app speaks, English first — `echo_wire::lang::ALL`
 * (pets-plan.md D351): the order a form offers a text per language in, and
 * the fallback after the reader's own. */
export const LANGS: readonly Lang[] = ["en", "ru"];

/** A text out of a map keyed by language tag (D351), read the way
 * `echo_wire::lang::pick` reads it: the reader's own language, then English,
 * then whichever is there, keys in their sorted order — so a text written
 * before a language existed, or a map filled only half-way, still shows
 * something. A blank text counts as missing, a tag's case does not count at
 * all. `null` when there is nothing to show. */
export function pickText(
  texts: Readonly<Record<string, string>> | null | undefined,
  lang: Lang,
): string | null {
  if (!texts) return null;
  const keys = Object.keys(texts).sort();
  const filled = (key: string) => (texts[key] ?? "").trim() !== "";
  const own = (tag: string) => keys.find((key) => key.toLowerCase() === tag && filled(key));
  const key = own(lang) ?? own("en") ?? keys.find(filled);
  return key === undefined ? null : (texts[key] ?? null);
}

/** The language a dictionary speaks — for the few things a component picks by
 * language rather than has worded for it, a pet's texts above all. */
export function langOf(t: Dict): Lang {
  return t === ru ? "ru" : "en";
}

/** «по-английски» — how a Russian label says which language a field is for. */
function inRussian(tag: string): string {
  switch (tag) {
    case "en":
      return "по-английски";
    case "ru":
      return "по-русски";
    default:
      return tag;
  }
}

/** "English" — the same for an English label. */
function inEnglish(tag: string): string {
  switch (tag) {
    case "en":
      return "English";
    case "ru":
      return "Russian";
    default:
      return tag;
  }
}

/** The Russian of every refusal of the sticker format's own lines (D546,
 * D564), keyed by the gateway's English — `gateway/src/locale.rs` and the
 * desktop's `Lang::sticker_rejected` say the same. */
const STICKER_REJECTED_RU: Readonly<Record<string, string>> = {
  "a sticker may be at most 64 KiB": "Стикер — не больше 64 КБ",
  "not a TGS sticker": "Это не стикер TGS",
  "the sticker file is damaged": "Файл стикера повреждён",
  "a sticker may unpack to at most 2 MiB": "Стикер слишком большой внутри: не больше 2 МБ",
  "not a Lottie animation": "Это не анимация Lottie",
  "a sticker must be 64 to 1024 points on each side": "Стороны стикера — от 64 до 1024 точек",
  "a sticker may run at 1 to 60 frames a second": "Частота кадров стикера — от 1 до 60 в секунду",
  "a sticker may last at most 3 seconds": "Стикер — не длиннее 3 секунд",
  "a sticker may not carry pictures inside": "В стикере не может быть картинок",
  "this animation cannot be played": "Эту анимацию нельзя проиграть",
  "a still sticker must be WebP or PNG": "Статичный стикер — только WebP или PNG",
  "a still sticker may be at most 128 KiB": "Статичный стикер — не больше 128 КБ",
  "a still sticker must be 128 to 512 pixels on its long side":
    "Длинная сторона статичного стикера — от 128 до 512 пикселей",
  "animated pictures must be TGS": "Анимированные картинки — только TGS",
  "a sticker's poster must be a picture of at most 64 KiB":
    "Обложка стикера — картинка не больше 64 КБ",
};

/** A name a device gave itself, set apart from the sentence around it (U+2068
 * … U+2069, the desktop's `sessions_end_one_confirm`): one written right to
 * left, or holding a direction mark, must not turn the sentence around. */
function isolated(name: string): string {
  return `${String.fromCodePoint(0x2068)}${name}${String.fromCodePoint(0x2069)}`;
}

/** A sum of Floats in words, as the desktop's `Lang::floats_amount` says it
 * (docs/floats-plan.md D662, D959): the digits grouped by threes — a narrow
 * no-break space in Russian, a comma in English — and «1 Float» for one in
 * English. */
function floats(n: number, lang: Lang): string {
  const digits = String(Math.abs(Math.trunc(n)));
  let number = n < 0 ? "\u{2212}" : "";
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) number += lang === "ru" ? "\u{202F}" : ",";
    number += digits[i];
  }
  return lang === "en" && Math.abs(n) === 1 ? `${number} Float` : `${number} Floats`;
}

/** A short text in both languages, side by side the way i18n.rs pairs them. */
type Words = Readonly<Record<Lang, string>>;

/**
 * The tips (docs/tooltips-plan.md D721–D731): the name of every control that
 * has no words of its own — its tooltip and its `aria-label`, one string
 * (D723, D730). One or two words, at most 24 characters, on one line, in both
 * languages, held by `i18n.test.ts`. This is the twin of the `tips!` block in
 * the desktop's i18n.rs, line for line and word for word, and the test reads
 * that block to keep it so. A toggle's tip names what a press will do, so it
 * comes in pairs. A person's, a pack's or a tone's own name is not a tip and
 * is not here.
 */
export const TIPS = {
  // Anywhere.
  tipClose: { ru: "Закрыть", en: "Close" },
  tipBack: { ru: "Назад", en: "Back" },
  tipCancel: { ru: "Отменить", en: "Cancel" },
  tipClear: { ru: "Очистить", en: "Clear" },
  tipDismiss: { ru: "Скрыть", en: "Dismiss" },
  tipRemove: { ru: "Убрать", en: "Remove" },
  tipDelete: { ru: "Удалить", en: "Delete" },
  tipCopy: { ru: "Копировать", en: "Copy" },
  tipRetry: { ru: "Повторить", en: "Try again" },
  tipRefresh: { ru: "Обновить", en: "Refresh" },
  tipActions: { ru: "Действия", en: "Actions" },
  tipProfile: { ru: "Профиль", en: "Profile" },
  tipDetails: { ru: "Детали", en: "Details" },
  tipSearch: { ru: "Поиск", en: "Search" },
  tipSettings: { ru: "Настройки", en: "Settings" },
  tipFeedback: { ru: "Отправить отзыв", en: "Send feedback" },
  // The plus beside the search: a menu of what can be made (D306).
  tipCreate: { ru: "Создать", en: "Create" },
  tipUnpin: { ru: "Открепить", en: "Unpin" },
  tipVolume: { ru: "Громкость", en: "Volume" },
  tipPrevious: { ru: "Предыдущее", en: "Previous" },
  tipNext: { ru: "Следующее", en: "Next" },
  tipSelect: { ru: "Выбрать", en: "Select" },
  tipDeselect: { ru: "Снять выбор", en: "Deselect" },
  tipShowPassword: { ru: "Показать пароль", en: "Show password" },
  tipHidePassword: { ru: "Скрыть пароль", en: "Hide password" },
  // Calls.
  tipCall: { ru: "Звонок", en: "Call" },
  tipConnecting: { ru: "Соединяемся…", en: "Connecting…" },
  tipCallOpen: { ru: "Открыть звонок", en: "Open call" },
  tipCallAccept: { ru: "Ответить", en: "Answer" },
  tipCallDecline: { ru: "Отклонить", en: "Decline" },
  tipMute: { ru: "Выключить микрофон", en: "Mute" },
  tipUnmute: { ru: "Включить микрофон", en: "Unmute" },
  tipDeafen: { ru: "Выключить звук", en: "Mute sound" },
  tipUndeafen: { ru: "Включить звук", en: "Unmute sound" },
  tipHangUp: { ru: "Отключиться", en: "Leave call" },
  tipParticipants: { ru: "Участники", en: "Participants" },
  tipCallPeople: { ru: "Участники звонка", en: "Call participants" },
  tipRateBad: { ru: "Плохо", en: "Bad" },
  tipRateGood: { ru: "Хорошо", en: "Good" },
  // The composer.
  tipAttach: { ru: "Прикрепить", en: "Attach" },
  tipEmoji: { ru: "Эмодзи", en: "Emoji" },
  tipVoice: { ru: "Голосовое сообщение", en: "Voice message" },
  tipSend: { ru: "Отправить", en: "Send" },
  tipSave: { ru: "Сохранить", en: "Save" },
  tipRecordingCancel: { ru: "Отменить запись", en: "Cancel recording" },
  tipRecordingSend: { ru: "Отправить запись", en: "Send recording" },
  tipPreviewRemove: { ru: "Убрать превью", en: "Remove preview" },
  tipUploadCancel: { ru: "Отменить отправку", en: "Cancel upload" },
  // The feed.
  tipJumpUnread: { ru: "К непрочитанным", en: "Unread messages" },
  tipJumpLatest: { ru: "К последним", en: "Latest messages" },
  tipPlay: { ru: "Слушать", en: "Play" },
  tipPause: { ru: "Пауза", en: "Pause" },
  tipSeek: { ru: "Перемотка", en: "Seek" },
  tipSelectionCancel: { ru: "Отменить выбор", en: "Cancel selection" },
  tipMoreReactions: { ru: "Ещё реакции", en: "More reactions" },
  // The picture viewer.
  tipDownload: { ru: "Скачать", en: "Download" },
  tipDownloading: { ru: "Скачиваем…", en: "Downloading…" },
  tipOpenFolder: { ru: "Открыть папку", en: "Open folder" },
  // A card's photo, which opens the viewer over every photo worn
  // (docs/avatar-history.md D757); the web draws no such door yet.
  tipOpenPhoto: { ru: "Открыть фото", en: "Open photo" },
  // Stickers and emoji. The emoji panel's tabs, shorter than the headings
  // over their sections («Смайлики и эмоции»).
  tipStickerCatalog: { ru: "Каталог стикеров", en: "Sticker catalogue" },
  tipSuggestionsHide: { ru: "Скрыть подсказки", en: "Hide suggestions" },
  tipEmojiAdd: { ru: "Добавить эмодзи", en: "Add emoji" },
  tipEmojiRecent: { ru: "Недавние", en: "Recent" },
  tipEmojiSmileys: { ru: "Смайлики", en: "Smileys" },
  tipEmojiPeople: { ru: "Люди", en: "People" },
  tipEmojiAnimals: { ru: "Животные", en: "Animals" },
  tipEmojiFood: { ru: "Еда", en: "Food" },
  tipEmojiTravel: { ru: "Путешествия", en: "Travel" },
  tipEmojiActivities: { ru: "Занятия", en: "Activities" },
  tipEmojiObjects: { ru: "Предметы", en: "Objects" },
  tipEmojiSymbols: { ru: "Символы", en: "Symbols" },
  tipEmojiFlags: { ru: "Флаги", en: "Flags" },
  // People, pets, a group's links.
  tipCommandRemove: { ru: "Удалить команду", en: "Remove command" },
  tipShowToken: { ru: "Показать токен", en: "Show token" },
  tipHideToken: { ru: "Скрыть токен", en: "Hide token" },
  tipSessionEnd: { ru: "Завершить сеанс", en: "End session" },
  tipLinkNew: { ru: "Новая ссылка", en: "New link" },
  tipLinkCreate: { ru: "Создать ссылку", en: "Create link" },
  tipLinkDelete: { ru: "Удалить ссылку", en: "Delete link" },
  // Settings.
  tipAssign: { ru: "Назначить", en: "Assign" },
  tipListen: { ru: "Прослушать", en: "Play" },
  tipChooseFile: { ru: "Выбрать файл", en: "Choose file" },
  tipReset: { ru: "Сбросить", en: "Reset" },
  // The stream screen and its pop-out window.
  tipViewers: { ru: "Кто смотрит", en: "Viewers" },
  tipNoViewers: { ru: "Нет зрителей", en: "No viewers" },
  tipShareScreen: { ru: "Трансляция экрана", en: "Share screen" },
  tipShareStop: { ru: "Остановить показ", en: "Stop sharing" },
  tipStreamClose: { ru: "Закрыть стрим", en: "Close stream" },
  tipPopOut: { ru: "Отдельное окно", en: "Pop out" },
  tipPopIn: { ru: "В приложение", en: "Pop in" },
  tipFullScreen: { ru: "Полный экран", en: "Full screen" },
  tipFullScreenExit: { ru: "Обычный режим", en: "Exit fullscreen" },
  tipPinWindow: { ru: "Закрепить окно", en: "Pin window" },
  tipUnpinWindow: { ru: "Открепить окно", en: "Unpin window" },
  tipPeopleEarlier: { ru: "Предыдущие участники", en: "Earlier participants" },
  tipPeopleLater: { ru: "Следующие участники", en: "Later participants" },
  tipStreamVolume: { ru: "Громкость стрима", en: "Stream volume" },
  // The camera (camera-plan.md D699, D700): the dock's button and its menu,
  // and a camera's tile, expanded and back.
  tipCameraOn: { ru: "Включить камеру", en: "Start camera" },
  tipCameraOff: { ru: "Выключить камеру", en: "Stop camera" },
  tipCameraChoose: { ru: "Выбрать камеру", en: "Choose camera" },
  // Its twins beside the dock's microphone and sound (dioxus-plan.md D926).
  tipMicChoose: { ru: "Выбрать микрофон", en: "Choose microphone" },
  tipOutputChoose: { ru: "Выбрать динамики", en: "Choose speakers" },
  tipCameraExpand: { ru: "Развернуть", en: "Enlarge" },
  tipCameraCollapse: { ru: "Показать всех", en: "Show everyone" },
  // Broadcast channels (broadcast-channels-plan.md D851, D863).
  tipPostOptions: { ru: "Параметры поста", en: "Post options" },
  tipBackToChannel: { ru: "К каналу", en: "Back" },
  // The plus beside the balance on the dock (docs/floats-plan.md D960).
  tipTopUp: { ru: "Пополнить", en: "Top up" },
  // The top-up sheet's history, beside its cross (D963).
  tipFloatsHistory: { ru: "История", en: "History" },
} as const satisfies Record<string, Words>;

/** The tips of the controls only this client draws, held to a tip's length
 * like the rest. Apart from `TIPS` so that block stays the desktop's twin: a
 * tip the desktop comes to draw too moves there, in both files at once. */
export const WEB_TIPS = {
  // The account dock: the server administrator's reports, and the status
  // menu, which the desktop opens from the reader's own face (D182).
  tipReports: { ru: "Жалобы", en: "Reports" },
  tipStatus: { ru: "Статус", en: "Status" },
  // A friend request answered in the Friends tab's list; the desktop's list
  // opens the person's profile, where its two buttons have words.
  tipRequestAccept: { ru: "Принять заявку", en: "Accept request" },
  tipRequestDecline: { ru: "Отклонить заявку", en: "Decline request" },
  // The composer's door to the sticker panel, a tab of the emoji panel's on
  // the desktop.
  tipStickers: { ru: "Стикеры", en: "Stickers" },
  // The eye beside somebody sharing, in the call strip's roster.
  tipWatchStream: { ru: "Смотреть стрим", en: "Watch stream" },
  // A pet's commands, put in order.
  tipMoveUp: { ru: "Выше", en: "Move up" },
  tipMoveDown: { ru: "Ниже", en: "Move down" },
  // The reader's own face on the profile card.
  tipChangePhoto: { ru: "Изменить фото", en: "Change photo" },
} as const satisfies Record<string, Words>;

/** Every tip as a method of the dictionary, the way every other sentence is
 * one: `t.tipClose()` is the desktop's `t.tip_close()`. */
type Tips = { readonly [K in keyof typeof TIPS | keyof typeof WEB_TIPS]: () => string };

function tipsIn(lang: Lang): Tips {
  const all: Record<string, Words> = { ...TIPS, ...WEB_TIPS };
  return Object.fromEntries(
    Object.entries(all).map(([name, words]) => [name, () => words[lang]]),
  ) as Tips;
}

export interface Dict extends Tips {
  // Login / register
  loginTitle(): string;
  loginUsername(): string;
  loginPassword(): string;
  loginEmail(): string;
  loginSubmit(): string;
  registerSubmit(): string;
  loginSwitchToRegister(): string;
  loginSwitchToLogin(): string;
  loginAgeGate(): string;
  loginFailed(reason: string): string;
  loginForgot(): string;

  // Confirming an address — the six digits, and the two flows that spend them
  // (gateway/src/confirm.rs).
  codeTitle(): string;
  /** No address in it: an address is never shown (owner rule, 21.09). */
  codeSent(): string;
  codeField(): string;
  codeSubmit(): string;
  codeResend(): string;
  /** «Send it again» is not ready yet: the letter went out `seconds` ago. */
  codeResendIn(seconds: number): string;
  /** How much of the five minutes is left, as m:ss. */
  codeExpiresIn(seconds: number): string;
  codeExpired(): string;
  codeBad(): string;
  codeBurnt(): string;
  codeBack(): string;
  recoverTitle(): string;
  recoverLead(): string;
  /** The recovery field: an address or a username. */
  recoverLogin(): string;
  /** Recovery's code screen. Not `codeSent`: whoever typed a username did
   * not enter the address the letter went to, and is not told it either. */
  recoverCodeSent(): string;
  recoverSubmit(): string;
  recoverNewPassword(): string;
  recoverRepeatPassword(): string;
  recoverSave(): string;
  recoverDone(): string;
  passwordsDiffer(): string;
  /** The server has no relay (501), or is older than the whole flow (404). */
  mailUnavailable(): string;

  // What the client says about a request itself (D339) — the server's own
  // refusals arrive already in this language, as `message`.
  errorNotLoggedIn(): string;
  errorTimeout(): string;
  /** The request never got an answer: offline, DNS, a blocked connection. */
  errorNetwork(): string;
  /** A refusal whose body was not the server's (a proxy's page). */
  errorHttp(status: number): string;
  errorBadAnswer(): string;
  /** D311: signed in, but nothing opens until the account proves an address. */
  addressLead(): string;
  addressSubmit(): string;
  addressSignOut(): string;

  // Profile
  profileOpen(): string;
  profileChangePhoto(): string;
  profileDisplayName(): string;
  profileBio(): string;
  profileChangePassword(): string;
  profileChangeEmail(): string;
  /** The statistics switch (analytics-plan.md D437). Never «anonymous»: the
   * per-day records it stops are about the account. */
  profileAnalytics(): string;
  /** What turning it off does, said in the row. */
  profileAnalyticsHint(): string;
  /** The link at the card's foot to the notices of what the client is built
   * with (docs/stickers-plan.md: tlottie's MIT notice travels with the web
   * build, which has no licences page of its own). */
  profileNotices(): string;

  // Changing the e-mail (D312): a code to the address on record, then one to
  // the new address. The code field, its countdown and its two refusals are
  // the `code*` keys above.
  emailTitle(): string;
  /** The label over the masked address on record. */
  emailCurrent(): string;
  /** An account with no address on record (a stand whose gate is off). */
  emailNone(): string;
  emailCurrentLead(): string;
  emailRequestCode(): string;
  /** The request button while the minute since the last letter runs. */
  emailRequestCodeIn(seconds: number): string;
  emailSentCurrent(): string;
  emailNext(): string;
  emailNew(): string;
  emailNewLead(): string;
  emailSentNew(): string;
  emailSave(): string;
  /** The toast at the end. */
  emailChanged(): string;
  /** 409: somebody else's address, or the one already on record. */
  emailTaken(): string;
  emailBad(): string;
  /** 429 without `field: "code"`: the per-address or per-connection budget. */
  emailTooMany(): string;
  /** The ten minutes the old mailbox's code bought ran out. */
  emailChangeExpired(): string;
  /** A gateway older than D312 answered 404: not a mail problem. */
  emailChangeUnsupported(): string;

  // Shell / sidebar
  channels(): string;
  allTab(): string;
  friendsTab(): string;
  searchPlaceholder(): string;
  /** The sidebar's row that makes an ordinary channel. It stands beside
   * `newGroup` (D274), so the two must never read the same: the desktop
   * hides its channel row while channels cannot be made (D293), the web
   * still draws it and shows the gateway's 403. */
  newChannel(): string;
  /** A group (D274): a channel private from birth and for good. */
  newGroup(): string;
  /** A gateway older than groups answered `POST /v1/groups` with 404 and made nothing. */
  groupUnsupported(): string;
  /** The fourth tab (pets-plan.md D361): the dialogs with pets. */
  petsTab(): string;
  /** The row that opens the new pet's form, for whoever may make one (D363). */
  newPet(): string;
  /** The Pets tab with nothing searched for on a server with no live pet (D405). */
  petsEmpty(): string;
  /** The caption between the dialogs with pets and every other live pet (D405). */
  petsMore(): string;
  /** The Pets tab's search found no pet. */
  petsNotFound(): string;
  /** What the paw beside a pet's name says to a screen reader. */
  petMark(): string;
  /** The mark before a friend's name in a chat list (dioxus-plan.md D1097). */
  friendMark(): string;
  /** A gateway older than pets answered `POST /v1/pets` with 404 and made nothing. */
  petsUnsupported(): string;
  members(n: number): string;
  connecting(): string;
  reconnecting(): string;
  offline(): string;

  // Feed
  today(): string;
  yesterday(): string;
  unreadSeparator(): string;
  edited(): string;
  unsupportedMessage(): string;
  unsupportedFormatting(): string;
  unsupportedAttachment(): string;
  /** The tile a sticker of a kind this build cannot name stands as in a
   * pack's grid (docs/stickers-plan.md D609) — the desktop's
   * `unsupported_update_hint`. */
  stickerUnknownHint(): string;
  /** A message with no words of its own, summed up in the sidebar row —
   * «📷 2 фото», «🖼 3 медиа» for photos and videos together; the desktop's
   * `preview_photo` and its siblings. A voice message is its length,
   * «🎤 0:07» (D494), and the word only when its sender declared none. */
  mediaPreview(
    kind: "photo" | "video" | "media" | "video_message" | "voice",
    count: number,
    durationMs?: number | null,
  ): string;
  loadOlder(): string;
  emptyChannel(): string;
  /** Where in an album a dot takes the reader, «2 из 5»: the dot's name and
   * its tip (docs/tooltips-plan.md D723) — the desktop's `album_position`. */
  albumPosition(index: number, total: number): string;
  /** The press over a picture under a spoiler that shows it — the desktop's
   * `album_reveal`. */
  albumReveal(): string;
  /** The viewer a picture opens in, as a screen reader names it — the
   * desktop's `viewer_photo`. */
  viewerPhoto(): string;

  // Composer
  composerPlaceholder(): string;
  /** The heading over the composer's `@` list (dioxus-plan.md D284) — the
   * desktop's `channel_members`. */
  mentionHeading(): string;
  /** The heading over the composer's `/` list (pets-plan.md D360). */
  commandHeading(): string;
  /** The one button a stopped pet's dialog has for a composer (D368): it
   * sends `/restart`. The card's verb says the same. */
  petRestart(): string;
  /** A retired pet's dialog, in the composer's place (D365). */
  petRetired(): string;
  /** A former friend's conversation, in the composer's place
   * (friends-plan.md D411). Nobody is named as having ended it — nobody is
   * told who did (D170) — so the line is about the two of them. */
  dmFormerTitle(): string;
  dmFormerHint(): string;
  /** «Delete chat» (D412), on that bar and as the question's title — and on
   * a live dialog's row menu, where it clears the chat for the reader alone
   * (friends-plan.md D465). */
  dmForget(): string;
  /** The question before it — honest about the second delete: when the
   * other side has deleted theirs already, this one deletes the history. */
  dmForgetBody(): string;
  /** The question before «Delete chat» on a live dialog (D465). Nobody is
   * named — a display name has a case we do not know — and nothing says
   * «friends», because a dialog with a pet asks it too. */
  dmClearBody(): string;
  composerJoinFirst(): string;
  joinChannel(): string;
  slowModeWait(seconds: number): string;
  messageTooLongSplit(parts: number): string;
  send(): string;
  recordingCancelled(): string;
  /** The paperclip, and its two doors (D397): a picture or a film goes as
   * what it is — a picture in two light weights (D233) — and a file goes
   * untouched, whatever it is. Telegram's words for the same two. */
  attach(): string;
  attachMedia(): string;
  attachFile(): string;
  /** A staged picture's or film's chip opens it in the lightbox before it is
   * sent (D397). */
  viewAttachment(): string;
  /** A picture past the original's budget through the «File» door (D233),
   * with the way out: the other door. */
  originalOverLimit(maxBytes: number): string;
  /** The pill over the composer while a send's attachments go up
   * (chat-plan.md D398): what is going, counted by the kind it travels as —
   * «Отправка 2 фото» — and, once two kinds meet, «Отправка медиа» with no
   * number. */
  uploadPhotos(count: number): string;
  uploadVideos(count: number): string;
  uploadFiles(count: number): string;
  uploadMedia(): string;
  /** «3,3 из 8,2 МБ» — both numbers in the total's unit (D398). */
  uploadAmount(done: number, total: number): string;
  /** The tooltip of the pill's cross (D398). */
  uploadCancel(): string;
  /** Who is typing (chat-plan.md D464), the words before the three dots that
   * stand in for a chat's subtitle and for its row's last message: the verb
   * alone in a dialog, whose header and row already name the peer; in a group
   * one name, two names, and from three on «several people» rather than a
   * count. */
  typing(dialog: boolean, names: string[]): string;

  // Auto-deleted messages (docs/disappearing-messages.md D296–D303). A span of
  // time is said three ways, and `lib/ttl.ts` decides the unit and the number
  // for all of them: `ttlLong` in words («30 секунд») for the menu,
  // `ttlShort` as a label («30 с», «1 м» — one letter for every unit, the
  // owner's word, because it is a mark on a button), `ttlHoursLeft` for a
  // countdown still an hour or more away — under the hour it is `m:ss`, which
  // has no words. `ttlAfter` is `ttlLong` inside a sentence, after «через»,
  // which takes the accusative — «через 1 минуту» — where the menu's own
  // row reads «1 минута»; the desktop's `ttl_long` and `ttl_after`.
  ttlLong(n: number, unit: TtlUnit): string;
  ttlAfter(n: number, unit: TtlUnit): string;
  ttlShort(n: number, unit: TtlUnit): string;
  ttlHoursLeft(hours: number): string;
  /** The send button's tooltip, and the mark on its bottom edge, while a
   * timer is set. */
  ttlClockOn(lifetime: string): string;
  /** The box's placeholder while a timer is set, and what a message waiting
   * for its first reader says about itself (D302). */
  ttlPlaceholder(lifetime: string): string;
  ttlAfterRead(lifetime: string): string;
  /** The one row of the send button's own menu, which opens the lifetimes
   * beside it, and the row of that submenu that turns the timer off. */
  ttlAutoDelete(): string;
  ttlTurnOff(): string;
  /** A bubble's countdown as a tooltip, and the foot of a message's menu. */
  ttlDisappearsIn(left: string): string;
  /** A lifetime was asked for and the answer carries no deadline: a gateway
   * older than `ttl_s` ignored it, and the sender has to be told (D296). */
  ttlUnsupported(): string;

  // The user card (F3)
  cardAdd(): string;
  cardPending(): string;
  cardCancelRequest(): string;
  cardAccept(): string;
  cardDecline(): string;
  cardMessage(): string;
  cardUnfriend(): string;
  cardBlock(): string;
  cardUnblock(): string;
  cardPolicyClosed(): string;
  requestsTitle(): string;
  toastRequest(who: string): string;
  toastAccepted(who: string): string;
  /** A person's card (D282): copies their `/u/<id>` address into the web
   * client; the notice after it is `consoleInviteCopied`. */
  copyLink(): string;
  /** The last tile on a person's and a pet's card (dioxus-plan.md D765): the
   * rest of the card's verbs and the dialog's own menu. */
  cardMore(): string;

  // Pets (pets-plan.md D349–D368). What already has its words elsewhere is
  // reused: «Написать» is `cardMessage`, «Изменить» `menuEdit`, «Копировать»
  // `consoleInviteCopy`, the photo's two buttons `profileChangePhoto` and
  // `consoleBgRemove`, a sheet's `consoleCancel` / `consoleDone`.
  petStop(): string;
  /** On the card of a pet the reader stopped (D368). */
  petStoppedNote(): string;
  petAddToGroup(): string;
  /** The Add-to-group menu with nothing in it: no group where the reader is
   * an admin (D359). */
  petNoGroups(): string;
  petAdded(group: string): string;
  petOwner(name: string): string;
  petToken(): string;
  /** Retire (D365) — its confirmation is the same word with a `?`. */
  petRetire(): string;
  petRetireWarning(): string;
  petEditTitle(name: string): string;
  /** The public name chats draw (D406), the sheet's first field. */
  petDisplayName(): string;
  /** Under it: what it is for, and what an empty one shows. */
  petDisplayNameHint(): string;
  /** The nickname (D350, D384): `@` and `/cmd@` address it. */
  petNickname(): string;
  /** One description per language (D351), `tag` being which. */
  petDescription(tag: string): string;
  petCommandName(): string;
  petCommandDescription(tag: string): string;
  petAddCommand(): string;
  petRemoveCommand(): string;
  petMoveUp(): string;
  petMoveDown(): string;
  petJoinable(): string;
  petPhotoTooLarge(mib: number): string;
  petPhotoNotImage(): string;
  petTokenTitle(): string;
  /** Over a token just made or rotated (D357): it is never shown again. */
  petTokenOnce(): string;
  /** Over the token sheet opened from the card, with no token to show. */
  petTokenLost(): string;
  petTokenRotate(): string;
  petTokenCopied(): string;

  // Link cards (docs/link-previews.md D279). What already has its words
  // elsewhere is reused, not repeated: «Вступить» is `joinChannel`,
  // «Написать» `cardMessage`, «Заявка отправлена» `cardPending`, «Открыть
  // профиль» `profileOpen` and «Закрытый канал» `consolePrivate`.
  linkKindChannel(): string;
  linkKindGroup(): string;
  linkKindDm(): string;
  linkKindUser(): string;
  linkOpenChannel(): string;
  linkOpenGroup(): string;
  linkOpenChat(): string;
  linkAddFriend(): string;
  linkAcceptRequest(): string;
  /** The bar over the composer while the draft holds a link (D289): its
   * accent line while the page is still being read, the line under it being
   * `loading`. */
  linkPreview(): string;
  /** The bar's cross: the message goes without its card (D289). */
  linkPreviewDismiss(): string;
  /** Something asked for and not yet here — the desktop's `loading`. */
  loading(): string;

  // Calls (read-only, F3)
  ringIncoming(who: string, channel: string): string;
  ringJoinHint(): string;
  ringDecline(): string;
  callPill(n: number): string;
  /** The handset on a group's row while its call runs, as its tip: how many
   * are in it — the desktop's `in_call_n`. */
  inCallN(n: number): string;

  // Presence (presence-plan.md D187, D269). The words the status dot carries
  // as its tooltip — the dot has one bit of resolution, and the sentence is
  // where the rest of the answer goes. `presenceInCallAt` is its own string
  // rather than `presenceInCall` with a blank in it: the server never says
  // where a call is, so a channel this client cannot see is named nowhere, and
  // "in a call in «»" would be a sentence with a hole in it.
  presenceOnline(): string;
  presenceAfk(): string;
  presenceDnd(): string;
  presenceInvisible(): string;
  presenceInCall(): string;
  presenceInCallAt(channel: string): string;

  // Voice (F4)
  voiceJoin(): string;
  voiceConnecting(): string;
  voiceKeyExchange(): string;
  voiceLiveRtt(ms: number): string;
  voiceMutedState(): string;
  voiceDeafenedState(): string;
  voicePoorLoss(pct: number): string;
  voicePoorRtt(ms: number): string;
  voiceMute(): string;
  voiceUnmute(): string;
  voiceDeafen(): string;
  voiceUndeafen(): string;
  voiceDisconnect(): string;
  /** R16, said where the code is shown: voice is E2EE, the text chat is not. */
  voiceE2eeNote(): string;
  voiceUnlisted(): string;
  voiceMicDenied(): string;
  voiceUnsupported(): string;
  voiceDropped(): string;
  /** The question after a call (docs/call-rating.md D516): the body names
   * what is kept with the press, which is the whole of the consent; the two
   * answers are words only for the tooltip and the screen reader. */
  callRatingTitle(): string;
  callRatingBody(): string;
  callRatingBad(): string;
  callRatingGood(): string;
  callRatingClose(): string;
  callRatingThanks(): string;

  // Streams (F5)
  watchStream(): string;
  /** The call room (call grid) — where the strip's name and the header's pill lead. */
  callRoomOpen(): string;
  callRoomClose(): string;
  stopWatching(): string;
  streamOf(name: string): string;
  streamPaused(): string;
  /** Under a stream larger or faster than a browser shows it (D734). */
  streamCapNote(): string;
  shareVolume(): string;
  /** The pill with the number watching (D336): what a press opens, and the
   * pill with nobody to list — the desktop's i18n.rs says the same. */
  streamAudienceOpen(): string;
  streamAudienceNobody(): string;
  /** The heading over the list it opens, «Зрители — 3». */
  streamAudienceHeading(n: number): string;
  streamEndedStopped(): string;
  streamEndedSourceGone(): string;
  streamEndedLeft(): string;
  streamEndedAdmin(): string;

  // Cameras (camera-plan.md D703): the browser marks a camera that is on and
  // says where its picture is — it neither sends nor shows one.
  cameraOnMark(): string;

  // Channel console (F6)
  consoleTitle(name: string): string;
  consoleDone(): string;
  consoleCancel(): string;
  consoleAccess(): string;
  consolePrivate(): string;
  /** A group's access row — no switch, because nothing can open it (D274). */
  groupPrivate(): string;
  groupPrivateHint(): string;
  consoleLimits(): string;
  consoleVoiceLimit(s: number): string;
  consoleMediaLimit(mb: number): string;
  consoleCharsLimit(n: number): string;
  consoleSlowmode(s: number): string;
  consoleVoiceEnabled(): string;
  consoleInvite(): string;
  consoleInviteNone(): string;
  consoleInviteCreate(): string;
  consoleInviteCopy(): string;
  consoleInviteCopied(): string;
  consoleInviteRefresh(): string;
  consoleInviteRevoke(): string;
  consoleInviteTtl(): string;
  consoleAppearance(): string;
  consoleBgLight(): string;
  consoleBgDark(): string;
  consoleBgPick(): string;
  consoleBgRemove(): string;
  consoleBgSet(): string;
  consoleBgNone(): string;
  consoleBgWillRemove(): string;
  consoleBgBlur(n: number): string;
  consoleBgBrightness(n: number): string;
  consolePeople(): string;
  consoleRoleOwner(): string;
  consoleRoleAdmin(): string;
  consoleMakeAdmin(): string;
  consoleRevokeAdmin(): string;
  consoleMakeOwner(): string;
  consoleVoiceMute(): string;
  consoleVoiceUnmute(): string;
  consoleKick(): string;
  consoleBanDay(): string;
  consoleBanForever(): string;
  consoleBanned(): string;
  consoleBanUntil(when: string): string;
  consoleBanPermanent(): string;
  consoleUnban(): string;

  // Reports (F6)
  menuReport(): string;
  reportTitle(): string;
  reportReasonLabel(key: string): string;
  reportCommentPlaceholder(): string;
  reportSend(): string;
  reportSent(): string;
  reportsTitle(): string;
  reportsOpen(n: number): string;
  reportsShowResolved(): string;
  reportsRemove(): string;
  reportsDismiss(): string;
  reportsEmpty(): string;

  // Stickers (docs/stickers-plan.md D546–D566) — the desktop's i18n.rs pairs
  stickersTab(): string;
  stickersRecent(): string;
  stickersEmpty(): string;
  stickersCatalog(): string;
  stickersSearch(): string;
  stickersPopular(): string;
  stickersNew(): string;
  stickersNoneFound(): string;
  stickersLoadFailed(): string;
  /** The press under «Could not load» that asks again — the desktop's
   * `retry`. */
  stickersRetry(): string;
  stickersAdd(): string;
  stickersRemove(): string;
  stickersPackKind(): string;
  stickersPackGone(): string;
  /** Where a sticker of a pack taken down stood (D551). */
  stickerRemoved(): string;
  /** The stickers one emoji alone in the box offers (D574): what a screen
   * reader says of their row, and the cross that stops offering them. */
  stickerBarLabel(emoji: string): string;
  stickerBarHide(): string;
  stickersReportBody(): string;
  /** The two levels of D551, for a server administrator. */
  stickersTakeDown(): string;
  stickersTakeDownBody(): string;
  stickersBlockFiles(): string;
  stickersBlockFilesBody(): string;
  stickersMyPacks(): string;
  stickersTitle(): string;
  stickersTitleHint(): string;
  stickersCreate(): string;
  stickersDraft(): string;
  stickersPublished(): string;
  stickersTakenDown(): string;
  stickersAddSticker(): string;
  stickersFileHint(): string;
  stickersEmoji(): string;
  stickersEmojiHint(): string;
  stickersFileUnfit(): string;
  stickersEmptyPack(): string;
  /** D567: an own draft's sheet — it is neither installed nor sent. */
  stickersDraftHint(): string;
  stickersPublish(): string;
  stickersPublishTitle(): string;
  /** D558: the rules, and whose the art is, before the press. */
  stickersPublishBody(): string;
  stickersRules(): string;
  stickersDeletePack(): string;
  stickersDeletePackBody(): string;
  /** A sticker message's menu row and a click on it: its pack's sheet. */
  stickersViewPack(): string;
  /** The button of a link card to a pack (D562). */
  linkOpenPack(): string;
  stickersEdit(): string;
  stickersSave(): string;
  stickersCancel(): string;
  stickersClose(): string;
  stickersBack(): string;
  stickersDelete(): string;
  /** «Animated stickers» (D559). */
  stickersAnimated(): string;
  stickersAnimatedHint(): string;
  /** «Key hints on Ctrl» (docs/tooltips-plan.md D743), the desktop's
   * `settings_keytips` pair. */
  keytips(): string;
  keytipsHint(): string;
  /** «Клавиши» (docs/tooltips-plan.md D750): the map of the keys, read-only — the profile sheet's row and its third face. */
  keys(): string;
  keysNav(): string;
  keysChat(): string;
  keysCall(): string;
  keysTabNext(): string;
  keysTabPrev(): string;
  keysTabNumber(): string;
  keysChatNext(): string;
  keysChatNextHint(): string;
  keysChatPrev(): string;
  keysChatNumber(): string;
  /** Under the two rows that pick by number (D748): nothing opens before. */
  keysOnRelease(): string;
  /** The keycap that stands for a number typed in digits. */
  keysNumber(): string;
  /** Ctrl+S: on the web the profile sheet is the settings. */
  keysSettings(): string;
  keysNewLine(): string;
  keysToFeed(): string;
  keysToFeedHint(): string;
  keysWalk(): string;
  keysInside(): string;
  keysInsideHint(): string;
  keysEditOwn(): string;
  keysCloseHint(): string;
  keysCallToggle(): string;
  keysMute(): string;
  keysDeafen(): string;
  /** The web's push-to-talk: Ctrl+Space held while muted (D746). */
  keysTalk(): string;
  /** Under the map in a browser tab, which never hands Ctrl+T or Ctrl+N to a page (D747). */
  keysBrowserKeeps(): string;
  /** The card of the keys menus and panels answer to (docs/tooltips-plan.md D760, D763, D764). */
  keysMenus(): string;
  keysMenuRows(): string;
  keysFirstLast(): string;
  keysSubmenu(): string;
  keysPressRow(): string;
  keysPanels(): string;
  keysPanelsHint(): string;
  /** Enter on what has a menu of its own, and the keys of the message the walk is on (docs/tooltips-plan.md D804–D806). */
  keysFocusMenu(): string;
  keysFocusMenuHint(): string;
  keysMessageMenu(): string;
  keysReply(): string;
  keysForward(): string;
  keysPick(): string;
  keysCopyMessage(): string;
  keysDeleteMessage(): string;
  /** The admin's list of pack reports (D558). */
  stickersReports(): string;
  /** «😀» — a line about a sticker message, its own emoji; the word for one
   * that came without (D573). */
  stickerPreview(emoji: string): string;
  /** What a screen reader says of a sticker. */
  stickerLabel(emoji: string): string;
  /** «12 стикеров». */
  stickerCount(n: number): string;
  /** «Автор: Аня» (D557). */
  stickersBy(name: string): string;
  /** A refusal of the format's own lines (D546, D564), from the gateway's
   * English sentence — the editor's check speaks it, as the gateway's 400
   * does. */
  stickerRejected(english: string): string;

  // Message menu
  menuReact(): string;
  menuReply(): string;
  menuEdit(): string;
  menuDelete(): string;
  /** A deletion that goes from the reader's view alone, and everybody else
   * keeps the message (chat-plan.md D410, D407): the menu's row where nothing
   * more may be done, and one of the two answers where it may. */
  menuDeleteForMe(): string;
  /** The other answer (D407): the message goes from the chat, for everyone in
   * it. */
  menuDeleteForEverybody(): string;
  /** The header over a reply that carries nothing readable about what it
   * answers (a gateway older than D273). */
  replyElsewhere(): string;
  /** The header over a reply to a message since deleted (D273). */
  replyDeleted(): string;
  /** Elsewhere (forwarding.md D377): the message menu's row and the
   * selection bar's button — Telegram's own word for it. */
  menuForward(): string;
  /** Starts picking messages, with this one picked (D377). */
  menuSelect(): string;
  /** The row of an own message's menu whose submenu says who has read it,
   * and when (chat-plan.md D423). Asked of the gateway as it opens. */
  menuDetails(): string;
  /** That submenu, when nobody but the author has read the message yet. */
  detailsNobody(): string;
  /** …and when the gateway could not say. */
  detailsFailed(): string;
  /** The day under a reader's name, inside «… at 11:51» (`readAt`): lower
   * case, because the line goes on past it. */
  readToday(): string;
  readYesterday(): string;
  /** When a reader read it: «сегодня в 11:51» / «today at 11:51». */
  readAt(day: string, clock: string): string;
  /** The foot of an own message's menu in a direct conversation once the
   * other person has read it (chat-plan.md D466), while its time is on its way
   * — and for good when the gateway has none for that read. */
  menuRead(): string;
  /** …once it has, today: «Прочитано в 11:57». The day word is dropped, so
   * the common line fits the menu as it was drawn (`readLine`). */
  menuReadAt(clock: string): string;
  /** …and on an earlier day, `when` being `readAt`'s phrase: «Прочитано вчера
   * в 23:40», «Read August 5 at 09:02». */
  menuReadOn(when: string): string;
  /** The foot of an edited message's menu, for every reader, and the tooltip
   * of its pencil (chat-plan.md D467), when the last edit was today:
   * «Изменено в 11:57» (`editLine`). */
  menuEditedAt(clock: string): string;
  /** …and on an earlier day, `when` being `readAt`'s phrase: «Изменено вчера
   * в 23:40», «Edited August 5 at 09:02». */
  menuEditedOn(when: string): string;
  /** The line over a forward, before its author's face and name (D376). */
  forwardedFrom(): string;
  /** …and the whole line, over a forward whose header this build could not
   * read: the words are still not the sender's own. */
  forwardedUnknown(): string;
  /** The picker a forward is sent from (D377). */
  forwardTo(): string;
  /** The picker, with no chat in it the reader may write to. */
  forwardNowhere(): string;
  /** The picker's search, matching nothing. */
  forwardNoMatch(): string;
  /** The composer's bar over a forward: «Пересылка · Злата». */
  forwardOf(authors: string): string;
  /** The authors that bar names, at most two by name: «Злата, Боб и ещё 3». */
  forwardAuthors(names: string[]): string;
  /** The bar's second line over more than one message: «3 сообщения». */
  forwardCount(n: number): string;
  /** The selection bar (D377): «Выбрано: 3». */
  selectedCount(n: number): string;
  /** A gateway older than forwarding refused the send (chat-api.md §7):
   * nothing was made, and this is why. */
  forwardUnsupported(): string;
  /** The selection bar's copy button, on hover (docs/message-selection.md
   * D381). */
  selectionCopy(): string;
  /** The notice once a selection's words are on the clipboard (D381). */
  selectionCopied(): string;
  /** The second-long chip over a monospace run a click has just copied. */
  codeCopied(): string;
  /** The same chip over an `@handle` nobody answers to (dioxus-plan.md
   * D463). */
  tagNotFound(): string;
  /** …and when the browser would not take them. */
  copyFailed(): string;
  /** The question before a selection is deleted (D380): «Удалить 3
   * сообщения?», and one message is «Удалить сообщение?». */
  deleteMessagesTitle(n: number): string;
  /** …and what it does, under the question (chat-plan.md D407): where the `n`
   * may go for `everybody`, what each of the two answers does; where they may
   * not, that they go from the reader's view alone. */
  deleteMessagesBody(n: number, everybody: boolean): string;
  /** …and where the `n` may go for everybody alone
   * (docs/broadcast-channels-plan.md D868): a comment past its four hours
   * leaves nobody's view but everybody's, so the question offers no «for me»
   * and says why — the desktop's `delete_messages_body_everybody`. */
  deleteMessagesBodyEverybody(n: number): string;
  /** A gateway older than deleting a selection answered its route with a
   * 405: nothing was deleted, and this is why. */
  deleteManyUnsupported(): string;
  /** A gateway older than deleting for oneself (D407) has no route for it:
   * nothing was deleted — and nothing for everybody instead. */
  deleteForMeUnsupported(): string;
  /** A gateway older than deleting a chat for oneself (D465) has no route for
   * it: nothing was deleted. */
  deleteChatUnsupported(): string;

  // Service messages (D71) — sender first, then the object where one exists.
  svcCallStarted(who: string): string;
  svcCallLive(participants: number): string;
  svcCallEnded(duration: string | null): string;
  svcJoined(who: string): string;
  /** `joined` with `by` (pets-plan.md D359): a group's admin put `who` —
   * today only ever a pet — into the group. The present tense of the lines
   * around it, for their reason: a Russian past tense would have to guess who
   * the admin is. The desktop says the same words. */
  svcJoinedBy(by: string, who: string): string;
  svcLeft(who: string): string;
  svcRemoved(who: string, whom: string): string;
  svcAdminGranted(who: string, whom: string): string;
  svcAdminRevoked(who: string, whom: string): string;
  svcRenamed(who: string, name: string): string;
  svcBanned(who: string, whom: string, until: string | null): string;
  svcUnbanned(who: string, whom: string): string;
  /** `returned` (channel-admin-plan.md D487): a ban with a deadline ended
   * and `who` is back as they were — with `admin`, running the group again,
   * which the history has to say. Nobody acted, so nobody else is named.
   * The desktop says the same words. */
  svcReturned(who: string, admin: boolean): string;
  svcVoiceMuted(who: string, whom: string, until: string | null): string;
  svcVoiceUnmuted(who: string, whom: string): string;
  svcOwnerChanged(who: string, whom: string): string;
  svcUnknown(): string;
  /** Short, name-free sidebar/reply preview per service kind. A call's is
   * one of the four below (D540). */
  svcPreview(kind: string): string;
  /** A call's line while it runs (D540), with no icon in front (D542). */
  previewCallLive(): string;
  /** A call's line once over, with its length in the widget's words. */
  previewCallLasted(duration: string): string;
  /** A call that rang under a minute with nobody else in it. */
  previewCallUnanswered(): string;
  /** A call a gateway restart cut short: over, with no length. */
  previewCallInterrupted(): string;
  /** Who wrote a chat-list row's last message, when it was the reader
   * (D541); the colon after it is the row's. */
  previewYou(): string;

  // A link opened in the browser, offered to the desktop client (D313).
  desktopOfferTitle(): string;
  desktopOpen(): string;
  desktopOpening(): string;
  desktopNoApp(): string;
  desktopInstallTitle(): string;
  /** The try moved nothing: most likely there is no app to take the link. */
  desktopMissingTitle(): string;
  desktopStore(): string;
  desktopInstaller(): string;
  desktopHaveIt(): string;
  desktopDismiss(): string;

  // Broadcast channels (docs/broadcast-channels-plan.md §5.2, D863–D865):
  // the desktop's `i18n.rs` pairs, in the same words.
  /** The list's tab of broadcast channels (D863), and the search's section
   * of them (D858). */
  tabBroadcasts(): string;
  /** What kind of place a link leads into, and the card's line (D859). */
  broadcastKind(): string;
  broadcastPrivateKind(): string;
  broadcastOpen(): string;
  broadcastSubscribers(n: number): string;
  broadcastSubscribe(): string;
  broadcastUnsubscribe(): string;
  /** The foot of a channel read without a subscription (D859). */
  broadcastSubscribeHint(): string;
  /** «Канал · 12 подписчиков» — the card's line under the name (D864). */
  broadcastSubtitle(n: number): string;
  /** A subscription on its way (D838). */
  broadcastSubscribing(): string;
  /** «New channel»'s form (D836, D870, D872). */
  broadcastName(): string;
  broadcastPublic(): string;
  broadcastPublicHint(): string;
  broadcastPrivate(): string;
  broadcastPrivateHint(): string;
  broadcastHandle(): string;
  /** Where the handle leads: the link it makes (D870). */
  broadcastHandleHint(link: string): string;
  /** What a handle may be (D870, D732), under a handle that is not one. */
  broadcastHandleRule(): string;
  broadcastCreate(): string;
  broadcastNotFound(): string;
  broadcastsNotFound(): string;
  broadcastUnsupported(): string;
  /** The web's channel settings for this kind (D865). */
  broadcastSettingsDesktop(): string;
  /** The operator's mark (D857). */
  verifiedTip(): string;
  /** The publisher's composer (D841, D851). */
  broadcastPostPlaceholder(): string;
  postNoComments(): string;
  postNoReactions(): string;
  /** A post the composer will not split (D841): what it holds at most. */
  postTooLong(limit: number): string;
  /** A comment's face button (D848). */
  sendAsChannel(): string;
  sendAsSelf(): string;
  commentPlaceholder(): string;
  /** A post's view count (D1083): the exact number in words, for its tooltip
   * and its name, and the short form beside the time. */
  viewsCount(n: number): string;
  viewsShort(n: number): string;
  /** A post's comments (D846, D847, D849, D851, D853). */
  commentsCount(n: number): string;
  commentsLeave(): string;
  commentsTitle(): string;
  /** Back from a post's comments to its channel (D863). */
  commentsOff(): string;
  commentsSubscribe(): string;
  commentsRestricted(until: string): string;
  commentsRestrictedForever(): string;
  commentsEmpty(): string;
  /** A reply to the reader's comment (D861). */
  commentReplyToast(channel: string): string;
  /** A selection in a broadcast channel holding a message the reader may
   * not delete (D868, D1022): there everything goes for everybody or not at
   * all, so it is refused whole before the gateway would refuse it. */
  channelDeleteRefused(): string;
  /** A paste of a kind a thread's comment box does not offer (D924): the
   * channel's comments take none of it, or a restriction takes it from the
   * reader. It is not staged, and this is why. */
  commentKindRefused(): string;
  /** A post's menu (D842, D851, D859). */
  postCopyLink(): string;
  postLinkCopied(): string;
  menuCommentsOff(): string;
  menuCommentsOn(): string;
  menuReactionsOff(): string;
  menuReactionsOn(): string;
  postDetailsPosted(name: string): string;
  postDetailsEdited(name: string): string;
  /** A forward of a channel's words that is private now, or gone (D860); a
   * public one's line is «Переслано от» and the channel, as a person's is. */
  forwardedPrivateChannel(): string;
  /** A row of a kind this build cannot name (D862). */
  unsupportedChat(): string;

  // The official pet (docs/official-pet-plan.md §9.3)
  /** The verified mark in place of the paw (D939): its tip. */
  officialMark(): string;
  /** Where a pet's card names its owner (D935). */
  officialOwner(): string;
  /** The composer's place in its dialog (D947): the line, the offer. */
  officialBar(): string;
  officialOffer(): string;
  /** The dialog before its first message (D936). */
  officialEmpty(): string;
  /** A security notice's card (D942) and its toast (D943). */
  noticeSignin(): string;
  noticeRow(device: string): string;
  noticeToast(device: string): string;
  noticeMine(): string;
  noticeEnd(): string;
  noticeEnded(): string;
  noticePassword(): string;
  /** A notice of a kind this build cannot name (D940, D477). */
  noticeUnknown(): string;
  /** When, for a notice that came under a minute ago. */
  noticeJustNow(): string;
  /** A sign-in as its line names it (signins.md D502, D504): a device that
   * said nothing about itself. */
  sessionsUnknownDevice(): string;
  /** The question before a sign-in is ended (D504): which device, and what
   * happens to it — and its button. */
  sessionsEndConfirm(device: string): string;
  sessionsEndDo(): string;
  /** The feedback dialog (D231, D947): the feature's own name, what goes
   * with it, the box, the thanks. */
  feedback(): string;
  feedbackBody(): string;
  feedbackPlaceholder(): string;
  feedbackSent(): string;
  /** A promo code waiting (D981): the amount, and the date it ends when it
   * does. */
  floatsCode(amount: number, until: string | null): string;

  // Durations
  durationS(totalSeconds: number): string;
}

const ru: Dict = {
  ...tipsIn("ru"),
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Имя пользователя",
  loginPassword: () => "Пароль",
  loginEmail: () => "Почта",
  loginSubmit: () => "Войти",
  registerSubmit: () => "Создать аккаунт",
  loginSwitchToRegister: () => "Нет аккаунта? Регистрация",
  loginSwitchToLogin: () => "Уже есть аккаунт? Войти",
  loginAgeGate: () => "Мне есть 13 лет, и я принимаю правила",
  loginFailed: (reason) => reason,
  loginForgot: () => "Забыли пароль?",

  codeTitle: () => "Подтвердите почту",
  codeSent: () => "Мы отправили код на указанную почту",
  codeField: () => "Код из письма",
  codeSubmit: () => "Подтвердить",
  codeResend: () => "Отправить ещё раз",
  codeResendIn: (seconds) => `Отправить ещё раз можно через ${seconds} с`,
  codeExpiresIn: (seconds) => `Код действует ещё ${clock(seconds)}`,
  codeExpired: () => "Код истёк — запросите новый",
  codeBad: () => "Код неверный или истёк",
  codeBurnt: () => "Слишком много попыток. Начните заново.",
  codeBack: () => "Назад",
  recoverTitle: () => "Восстановление пароля",
  recoverLead: () => "Введите почту или ник — код придёт на почту аккаунта.",
  recoverLogin: () => "Почта или ник",
  recoverCodeSent: () => "Мы отправили код на почту аккаунта",
  recoverSubmit: () => "Отправить код",
  recoverNewPassword: () => "Новый пароль",
  recoverRepeatPassword: () => "Повторите пароль",
  recoverSave: () => "Сохранить пароль",
  recoverDone: () => "Пароль изменён — войдите с новым.",
  passwordsDiffer: () => "Пароли не совпадают",
  mailUnavailable: () => "Этот сервер не умеет отправлять письма.",
  errorNotLoggedIn: () => "Вы не вошли в аккаунт",
  errorTimeout: () => "Сервер не ответил вовремя",
  errorNetwork: () => "Нет связи с сервером. Проверьте подключение.",
  errorHttp: (status) =>
    status >= 500
      ? `Сервер не смог обработать запрос (HTTP ${status}). Попробуйте позже.`
      : `Сервер отклонил запрос (HTTP ${status})`,
  errorBadAnswer: () => "Сервер ответил непонятно. Обновите страницу.",
  addressLead: () =>
    "Чтобы продолжить, подтвердите почту: введите пароль от аккаунта и адрес — пришлём на него код из шести цифр.",
  addressSubmit: () => "Отправить код",
  addressSignOut: () => "Выйти",
  profileOpen: () => "Открыть профиль",
  profileChangePhoto: () => "Изменить фото",
  profileDisplayName: () => "Имя в приложении",
  profileBio: () => "О себе",
  profileChangePassword: () => "Сменить пароль",
  profileChangeEmail: () => "Сменить почту",
  profileAnalytics: () => "Отправлять статистику использования",
  profileAnalyticsHint: () =>
    "Если выключить, сервер перестанет вести записи о вашем аккаунте по дням и удалит те, что уже есть. Общие счётчики без привязки к людям — например, сколько сообщений отправлено за день, — ведутся всё равно.",
  profileNotices: () => "Лицензии сторонних компонентов",

  emailTitle: () => "Смена почты",
  emailCurrent: () => "Текущая почта",
  emailNone: () => "Почта не указана",
  emailCurrentLead: () => "Сначала подтвердите, что текущая почта ваша: пришлём на неё код.",
  emailRequestCode: () => "Запросить код",
  emailRequestCodeIn: (seconds) => `Запросить код можно через ${seconds} с`,
  emailSentCurrent: () => "Код отправлен на текущую почту",
  emailNext: () => "Далее",
  emailNew: () => "Новая почта",
  emailNewLead: () => "Теперь укажите новую почту — код придёт на неё.",
  emailSentNew: () => "Код отправлен на новую почту",
  emailSave: () => "Сменить почту",
  emailChanged: () => "Почта изменена",
  emailTaken: () => "Эта почта уже используется",
  emailBad: () => "Это не похоже на адрес почты",
  emailTooMany: () => "Слишком много запросов — попробуйте позже",
  emailChangeExpired: () => "Время на смену почты истекло — начните заново",
  emailChangeUnsupported: () => "Этот сервер пока не умеет менять почту — его нужно обновить.",

  channels: () => "Беседы",
  allTab: () => "Все",
  friendsTab: () => "Друзья",
  searchPlaceholder: () => "Поиск",
  newChannel: () => "Новый канал",
  newGroup: () => "Новая беседа",
  groupUnsupported: () => "Этот сервер пока не умеет создавать беседы.",
  petsTab: () => "Питомцы",
  newPet: () => "Новый питомец",
  petsEmpty: () => "Питомцев пока нет. Они появятся здесь, когда их заведут на сервере.",
  petsMore: () => "Ещё питомцы",
  petsNotFound: () => "Таких питомцев нет",
  petMark: () => "Питомец",
  friendMark: () => "Друг",
  petsUnsupported: () => "Этот сервер пока не умеет работать с питомцами.",
  members: (n) => `${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  connecting: () => "Подключение…",
  reconnecting: () => "Переподключение…",
  offline: () => "Нет соединения",

  today: () => "Сегодня",
  yesterday: () => "Вчера",
  unreadSeparator: () => "Непрочитанные",
  edited: () => "изменено",
  unsupportedMessage: () => "Обновите EchoFloat, чтобы открыть сообщение",
  unsupportedFormatting: () => "Часть оформления не показана",
  unsupportedAttachment: () => "Обновите EchoFloat, чтобы открыть вложение",
  stickerUnknownHint: () => "Обновите EchoFloat.",
  mediaPreview: (kind, count, durationMs) => {
    const many = count > 1 ? `${count} ` : "";
    switch (kind) {
      case "photo":
        return `📷 ${many}фото`;
      case "video":
        return `🎬 ${many}видео`;
      case "media":
        return `🖼 ${many}медиа`;
      case "video_message":
        return "⭕ видео-сообщение";
      case "voice":
        return durationMs != null ? `🎤 ${clock(durationMs / 1000)}` : "🎤 голосовое";
    }
  },
  loadOlder: () => "Ранее",
  emptyChannel: () => "Начните разговор",
  albumPosition: (index, total) => `${index} из ${total}`,
  albumReveal: () => "Показать",
  viewerPhoto: () => "Фото",

  composerPlaceholder: () => "Сообщение",
  mentionHeading: () => "Участники",
  commandHeading: () => "Команды",
  petRestart: () => "Перезапустить",
  petRetired: () => "Этот питомец больше не работает",
  dmFormerTitle: () => "Вы больше не друзья",
  dmFormerHint: () => "Переписку можно только читать",
  dmForget: () => "Удалить чат",
  dmForgetBody: () =>
    "Удалить чат? Он исчезнет из вашего списка. Если собеседник уже удалил его, переписка удалится навсегда.",
  dmClearBody: () =>
    "Вся переписка исчезнет только у вас — у собеседника она останется. Писать друг другу можно будет как прежде.",
  composerJoinFirst: () => "Вступите, чтобы писать",
  joinChannel: () => "Вступить",
  slowModeWait: (s) => `Подождите ${s} с`,
  messageTooLongSplit: (n) =>
    `Сообщение уйдёт ${n} ${plural("ru", n, "частью", "частями", "частями")}`,
  send: () => "Отправить",
  recordingCancelled: () => "Запись отменена",
  attach: () => "Прикрепить",
  attachMedia: () => "Фото или видео",
  attachFile: () => "Файл",
  viewAttachment: () => "Посмотреть",
  originalOverLimit: (b) =>
    `Файлом можно отправить картинку до ${
      Math.floor(b / (1024 * 1024))
    } МиБ — эта больше, прикрепите её как фото`,
  uploadPhotos: (n) => (n === 1 ? "Отправка фото" : `Отправка ${n} фото`),
  uploadVideos: (n) => (n === 1 ? "Отправка видео" : `Отправка ${n} видео`),
  // The genitive «отправка» governs: singular after a one, plural otherwise.
  uploadFiles: (n) =>
    n === 1 ? "Отправка файла" : `Отправка ${n} ${plural("ru", n, "файла", "файлов", "файлов")}`,
  uploadMedia: () => "Отправка медиа",
  uploadAmount: (done, total) => {
    const [a, b, unit] = amountIn("ru", done, total);
    return `${a} из ${b} ${unit}`;
  },
  uploadCancel: () => "Отменить отправку",
  typing: (dialog, names) =>
    dialog
      ? "печатает"
      : names.length === 1
        ? `${names[0]} печатает`
        : names.length === 2
          ? `${names[0]} и ${names[1]} печатают`
          : "Печатают несколько человек",

  ttlLong: (n, unit) => {
    switch (unit) {
      case "d": return `${n} ${plural("ru", n, "день", "дня", "дней")}`;
      case "h": return `${n} ${plural("ru", n, "час", "часа", "часов")}`;
      case "m": return `${n} ${plural("ru", n, "минута", "минуты", "минут")}`;
      default: return `${n} ${plural("ru", n, "секунда", "секунды", "секунд")}`;
    }
  },
  // Only the two feminine singulars differ from the nominative: «1 минуту»,
  // «21 секунду». Days and hours are masculine, and every other count is the
  // genitive both ways.
  ttlAfter: (n, unit) => {
    switch (unit) {
      case "m": return `${n} ${plural("ru", n, "минуту", "минуты", "минут")}`;
      case "s": return `${n} ${plural("ru", n, "секунду", "секунды", "секунд")}`;
      default: return ru.ttlLong(n, unit);
    }
  },
  ttlShort: (n, unit) => {
    switch (unit) {
      case "d": return `${n} д`;
      case "h": return `${n} ч`;
      case "m": return `${n} м`;
      default: return `${n} с`;
    }
  },
  ttlHoursLeft: (h) => `${h} ч`,
  ttlClockOn: (lifetime) => `Сообщения удаляются через ${lifetime} после прочтения`,
  ttlPlaceholder: (lifetime) => `Удалится через ${lifetime} после прочтения`,
  ttlAfterRead: (lifetime) => `Удалится через ${lifetime} после прочтения`,
  ttlAutoDelete: () => "Автоудаляемое сообщение",
  ttlTurnOff: () => "Без автоудаления",
  ttlDisappearsIn: (left) => `Удалится через ${left}`,
  ttlUnsupported: () => "Сервер ещё не умеет автоудаление — это сообщение останется",

  cardAdd: () => "В друзья",
  cardPending: () => "Заявка отправлена",
  cardCancelRequest: () => "Отменить",
  cardAccept: () => "Принять",
  cardDecline: () => "Отклонить",
  cardMessage: () => "Написать",
  cardUnfriend: () => "Удалить из друзей",
  cardBlock: () => "Заблокировать",
  cardUnblock: () => "Разблокировать",
  cardPolicyClosed: () => "Заявки закрыты",
  requestsTitle: () => "Заявки в друзья",
  toastRequest: (who) => `${who}: заявка в друзья`,
  toastAccepted: (who) => `${who}: заявка принята`,
  copyLink: () => "Скопировать ссылку",
  cardMore: () => "Ещё",

  petStop: () => "Остановить",
  petStoppedNote: () => "Вы остановили питомца: он вам не пишет, пока вы его не перезапустите.",
  petAddToGroup: () => "Добавить в беседу",
  petNoGroups: () => "Нет бесед, где вы администратор",
  petAdded: (group) => `Питомец добавлен в «${group}»`,
  petOwner: (name) => `Владелец: ${name}`,
  petToken: () => "Токен",
  petRetire: () => "Отключить",
  petRetireWarning: () =>
    "Токен перестанет работать, питомец покинет все беседы и больше никому не ответит. Имя останется занятым.",
  petEditTitle: (name) => `Питомец «${name}»`,
  petDisplayName: () => "Имя",
  petDisplayNameHint: () => "Так питомца видно в чатах. Если пусто — виден ник",
  petNickname: () => "Ник",
  petDescription: (tag) => `Описание ${inRussian(tag)}`,
  petCommandName: () => "команда",
  petCommandDescription: (tag) => `Что делает, ${inRussian(tag)}`,
  petAddCommand: () => "Добавить команду",
  petRemoveCommand: () => "Удалить команду",
  petMoveUp: () => "Выше",
  petMoveDown: () => "Ниже",
  petJoinable: () => "Можно добавлять в беседы",
  petPhotoTooLarge: (mib) => `Фото — не больше ${mib} МиБ`,
  petPhotoNotImage: () => "Это не webp, jpeg, png или avif",
  petTokenTitle: () => "Токен питомца",
  petTokenOnce: () =>
    "Скопируйте токен сейчас: больше его не покажут. С ним программа питомца входит в EchoFloat.",
  petTokenLost: () =>
    "Токен показывают один раз. Если он потерян, выпустите новый — старый сразу перестанет работать.",
  petTokenRotate: () => "Выпустить новый",
  petTokenCopied: () => "Токен скопирован",

  linkKindChannel: () => "Беседа",
  linkKindGroup: () => "Беседа",
  linkKindDm: () => "Личная переписка",
  linkKindUser: () => "Профиль",
  linkOpenChannel: () => "Открыть беседу",
  linkOpenGroup: () => "Открыть беседу",
  linkOpenChat: () => "Открыть чат",
  linkAddFriend: () => "Добавить в друзья",
  linkAcceptRequest: () => "Принять заявку",
  linkPreview: () => "Превью ссылки",
  linkPreviewDismiss: () => "Отправить без превью",
  loading: () => "Загрузка…",

  ringIncoming: (who, channel) => `${who} звонит в «${channel}»`,
  ringJoinHint: () => "Ответить",
  ringDecline: () => "Отклонить",
  callPill: (n) => `Звонок · ${n}`,
  inCallN: (n) => `${n} в звонке`,

  presenceOnline: () => "Онлайн",
  presenceAfk: () => "AFK",
  presenceDnd: () => "Не беспокоить",
  presenceInvisible: () => "Невидимка",
  presenceInCall: () => "В звонке",
  presenceInCallAt: (channel) => `В звонке в «${channel}»`,

  voiceJoin: () => "Войти",
  voiceConnecting: () => "Соединяемся…",
  voiceKeyExchange: () => "Шифруем…",
  voiceLiveRtt: (ms) => `Микрофон включён · ${ms} мс`,
  voiceMutedState: () => "Микрофон выключен",
  voiceDeafenedState: () => "Звук отключён",
  voicePoorLoss: (pct) => `Плохое соединение · потери ${pct.toFixed(0)}%`,
  voicePoorRtt: (ms) => `Плохое соединение · ${ms} мс`,
  voiceMute: () => "Выключить микрофон",
  voiceUnmute: () => "Включить микрофон",
  voiceDeafen: () => "Выключить звук",
  voiceUndeafen: () => "Включить звук",
  voiceDisconnect: () => "Выйти",
  voiceE2eeNote: () => "Звонок защищён сквозным шифрованием. Чат — нет",
  voiceUnlisted: () => "Ключи звонка есть у того, кого нет в списке. Вас не слышно",
  voiceMicDenied: () => "Разрешите доступ к микрофону",
  voiceUnsupported: () => "Обновите Chrome, чтобы звонить",
  voiceDropped: () => "Звонок оборвался",
  callRatingTitle: () => "Как прошёл звонок?",
  callRatingBody: () =>
    "Оцените качество связи. Оценка сохранится вместе с показателями связи этого звонка — задержкой и потерями.",
  callRatingBad: () => "Плохо",
  callRatingGood: () => "Хорошо",
  callRatingClose: () => "Закрыть",
  callRatingThanks: () => "Спасибо за оценку!",

  watchStream: () => "Смотреть стрим",
  callRoomOpen: () => "Открыть комнату звонка",
  callRoomClose: () => "К чату",
  stopWatching: () => "Закрыть стрим",
  streamOf: (name) => `Стрим ${name}`,
  streamPaused: () => "Стрим на паузе",
  streamCapNote: () => "В браузере — до 720p 60 fps. Полное качество — в приложении EchoFloat",
  shareVolume: () => "Громкость стрима",
  streamAudienceOpen: () => "Кто смотрит",
  streamAudienceNobody: () => "Пока никто не смотрит",
  streamAudienceHeading: (n) => `Зрители — ${n}`,
  streamEndedStopped: () => "Стрим завершён",
  streamEndedSourceGone: () => "Окно закрыто",
  streamEndedLeft: () => "Автор вышел из звонка",
  streamEndedAdmin: () => "Администратор остановил стрим",

  cameraOnMark: () => "Камера включена — её видно в приложении EchoFloat для Windows",

  consoleTitle: (name) => `«${name}»: настройки`,
  consoleDone: () => "Готово",
  consoleCancel: () => "Отмена",
  consoleAccess: () => "Доступ",
  consolePrivate: () => "Закрытая беседа",
  groupPrivate: () => "Закрытая беседа",
  groupPrivateHint: () => "Беседу видят только её участники, и открытой она не станет.",
  consoleLimits: () => "Лимиты",
  consoleVoiceLimit: (s) => `Голосовое до ${s} с`,
  consoleMediaLimit: (mb) => `Файлы: до ${mb} МБ`,
  consoleCharsLimit: (n) => `Сообщение до ${n} символов`,
  consoleSlowmode: (s) => (s === 0 ? "Медленный режим выключен" : `Пауза между сообщениями: ${s} с`),
  consoleVoiceEnabled: () => "Звонки",
  consoleInvite: () => "Приглашение",
  consoleInviteNone: () => "Ссылки нет",
  consoleInviteCreate: () => "Создать",
  consoleInviteCopy: () => "Копировать",
  consoleInviteCopied: () => "Ссылка скопирована",
  consoleInviteRefresh: () => "Новая ссылка",
  consoleInviteRevoke: () => "Удалить",
  consoleInviteTtl: () => "Ссылка действует 3 дня. Новая заменит старую",
  consoleAppearance: () => "Оформление",
  consoleBgLight: () => "Фон в светлой теме",
  consoleBgDark: () => "Фон в тёмной теме",
  consoleBgPick: () => "Выбрать",
  consoleBgRemove: () => "Убрать",
  consoleBgSet: () => "установлен",
  consoleBgNone: () => "нет",
  consoleBgWillRemove: () => "будет убран",
  consoleBgBlur: (n) => `Размытие фона: ${n}`,
  consoleBgBrightness: (n) => `Яркость фона: ${n}`,
  consolePeople: () => "Участники",
  consoleRoleOwner: () => "владелец",
  consoleRoleAdmin: () => "админ",
  consoleMakeAdmin: () => "Сделать админом",
  consoleRevokeAdmin: () => "Убрать из админов",
  consoleMakeOwner: () => "Передать беседу",
  consoleVoiceMute: () => "Выключить микрофон",
  consoleVoiceUnmute: () => "Включить микрофон",
  consoleKick: () => "Исключить",
  consoleBanDay: () => "Заблокировать на день",
  consoleBanForever: () => "Заблокировать навсегда",
  consoleBanned: () => "Заблокированные",
  consoleBanUntil: (when) => `до ${when}`,
  consoleBanPermanent: () => "навсегда",
  consoleUnban: () => "Разблокировать",

  menuReport: () => "Пожаловаться",
  reportTitle: () => "Жалоба",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Оскорбления";
      case "sexual": return "Откровенный контент";
      case "child": return "Опасность для детей";
      case "violence": return "Насилие";
      case "illegal": return "Незаконное";
      case "spam": return "Спам";
      default: return "Другое";
    }
  },
  reportCommentPlaceholder: () => "Комментарий (необязательно)",
  reportSend: () => "Отправить",
  reportSent: () => "Жалоба отправлена",
  reportsTitle: () => "Жалобы",
  reportsOpen: (n) => `Открытых: ${n}`,
  reportsShowResolved: () => "Показать закрытые",
  reportsRemove: () => "Удалено",
  reportsDismiss: () => "Отклонить",
  reportsEmpty: () => "Жалоб нет",

  stickersTab: () => "Стикеры",
  stickersRecent: () => "Недавние",
  stickersEmpty: () => "Здесь будут ваши стикеры. Найдите наборы в каталоге.",
  stickersCatalog: () => "Каталог стикеров",
  stickersSearch: () => "Поиск наборов",
  stickersPopular: () => "Популярные",
  stickersNew: () => "Новые",
  stickersNoneFound: () => "Ничего не нашлось",
  stickersLoadFailed: () => "Не удалось загрузить",
  stickersRetry: () => "Повторить",
  stickersAdd: () => "Добавить",
  stickersRemove: () => "Убрать",
  stickersPackKind: () => "Набор стикеров",
  stickersPackGone: () => "Этот набор недоступен",
  stickerRemoved: () => "Стикер удалён",
  stickerBarLabel: (emoji) => `Стикеры для ${emoji}`,
  stickerBarHide: () => "Не предлагать стикеры",
  stickersReportBody: () => "Администратор проверит набор. Что не так?",
  stickersTakeDown: () => "Снять набор",
  stickersTakeDownBody: () =>
    "Набор пропадёт из каталога и у всех, кто его добавил. Отправленные стикеры будут показаны как удалённые.",
  stickersBlockFiles: () => "Снять и заблокировать файлы",
  stickersBlockFilesBody: () =>
    "Только для незаконного контента: файлы удалятся с сервера и больше не загрузятся ни в одном чате.",
  stickersMyPacks: () => "Мои наборы",
  stickersTitle: () => "Название набора",
  stickersTitleHint: () => "Например, «Котики»",
  stickersCreate: () => "Создать",
  stickersDraft: () => "Черновик",
  stickersPublished: () => "Опубликован",
  stickersTakenDown: () => "Снят администратором",
  stickersAddSticker: () => "Добавить стикер",
  stickersFileHint: () => "TGS, Lottie JSON или .lottie до 3 секунд — или картинка PNG, WebP, JPEG",
  stickersEmoji: () => "Эмодзи стикера",
  stickersEmojiHint: () => "От одного до восьми — по ним стикер находят",
  stickersFileUnfit: () => "Файл не подходит",
  stickersEmptyPack: () => "В наборе пока нет стикеров",
  stickersDraftHint: () =>
    "Это черновик, его видите только вы. Опубликуйте набор, чтобы отправлять его стикеры.",
  stickersPublish: () => "Опубликовать",
  stickersPublishTitle: () => "Опубликовать набор?",
  stickersPublishBody: () =>
    "Набор увидят все. Публикуйте только свои рисунки или те, на которые у вас есть права: наборы из Telegram принадлежат их авторам.",
  stickersRules: () => "Правила публикации",
  stickersDeletePack: () => "Удалить набор",
  stickersDeletePackBody: () =>
    "Набор пропадёт из каталога и у всех, кто его добавил. Отправленные стикеры останутся в чатах.",
  stickersViewPack: () => "Набор стикеров",
  linkOpenPack: () => "Открыть набор",
  stickersEdit: () => "Изменить",
  stickersSave: () => "Сохранить",
  stickersCancel: () => "Отмена",
  stickersClose: () => "Закрыть",
  stickersBack: () => "Назад",
  stickersDelete: () => "Удалить",
  stickersAnimated: () => "Анимированные стикеры",
  stickersAnimatedHint: () => "Выключено — стикеры стоят на первом кадре.",
  keytips: () => "Подсказки клавиш по Ctrl",
  keytipsHint: () => "Зажмите Ctrl — у каждой кнопки появится её клавиша.",
  keys: () => "Клавиши",
  keysNav: () => "Переходы",
  keysChat: () => "Чат",
  keysCall: () => "Звонок",
  keysTabNext: () => "Следующая вкладка",
  keysTabPrev: () => "Предыдущая вкладка",
  keysTabNumber: () => "Вкладка по номеру",
  keysChatNext: () => "Следующий чат",
  keysChatNextHint: () => "В поле с текстом Ctrl+A выделяет текст.",
  keysChatPrev: () => "Предыдущий чат",
  keysChatNumber: () => "Чат по номеру",
  keysOnRelease: () => "Откроется, когда отпустите Ctrl.",
  keysNumber: () => "№",
  keysSettings: () => "Профиль и настройки",
  keysNewLine: () => "Новая строка",
  keysToFeed: () => "К сообщениям",
  keysToFeedHint: () => "Из начала поля ввода.",
  keysWalk: () => "По сообщениям",
  keysInside: () => "Внутрь сообщения",
  keysInsideHint: () => "Ссылки, реакции и последним — автор: Tab и стрелки, Esc — назад.",
  keysEditOwn: () => "Изменить своё сообщение",
  keysCloseHint: () => "Меню, окно, затем открытый чат.",
  keysCallToggle: () => "Позвонить, ответить или положить трубку",
  keysMute: () => "Микрофон",
  keysDeafen: () => "Звук",
  keysTalk: () => "Говорить, пока держите",
  keysBrowserKeeps: () => "Во вкладке браузера Ctrl+T и Ctrl+N занимает сам браузер — в установленном приложении они работают.",
  keysMenus: () => "Меню и панели",
  keysMenuRows: () => "По пунктам меню",
  keysFirstLast: () => "Первый и последний пункт",
  keysSubmenu: () => "Открыть или закрыть подменю",
  keysPressRow: () => "Нажать пункт",
  keysPanels: () => "По стикерам и реакциям",
  keysPanelsHint: () => "Enter выбирает; ↑ с верхнего ряда — к вкладкам наборов.",
  keysFocusMenu: () => "Меню того, что в фокусе",
  keysFocusMenuHint: () => "Сообщение, чат в списке; Space нажимает сам элемент.",
  keysMessageMenu: () => "Меню сообщения",
  keysReply: () => "Ответить на сообщение",
  keysForward: () => "Переслать сообщение",
  keysPick: () => "Выделить сообщение",
  keysCopyMessage: () => "Копировать сообщение",
  keysDeleteMessage: () => "Удалить сообщение",
  stickersReports: () => "Жалобы на наборы стикеров",
  stickerPreview: (emoji) => emoji || "Стикер",
  stickerLabel: (emoji) => `Стикер ${emoji}`.trimEnd(),
  stickerCount: (n) => `${n} ${plural("ru", n, "стикер", "стикера", "стикеров")}`,
  stickersBy: (name) => `Автор: ${name}`,
  stickerRejected: (english) => STICKER_REJECTED_RU[english] ?? english,

  menuReact: () => "Реакция",
  menuReply: () => "Ответить",
  menuEdit: () => "Изменить",
  menuDelete: () => "Удалить",
  menuDeleteForMe: () => "Удалить у себя",
  menuDeleteForEverybody: () => "Удалить у всех",
  replyElsewhere: () => "Более раннее сообщение",
  replyDeleted: () => "Сообщение удалено",
  menuForward: () => "Переслать",
  menuSelect: () => "Выбрать",
  menuDetails: () => "Детали",
  detailsNobody: () => "Пока никто не прочитал",
  detailsFailed: () => "Не удалось загрузить",
  readToday: () => "сегодня",
  readYesterday: () => "вчера",
  readAt: (day, clock) => `${day} в ${clock}`,
  menuRead: () => "Прочитано",
  menuReadAt: (clock) => `Прочитано в ${clock}`,
  menuReadOn: (when) => `Прочитано ${when}`,
  menuEditedAt: (clock) => `Изменено в ${clock}`,
  menuEditedOn: (when) => `Изменено ${when}`,
  forwardedFrom: () => "Переслано от",
  forwardedUnknown: () => "Пересланное сообщение",
  forwardTo: () => "Переслать в…",
  forwardNowhere: () => "Пока некуда переслать",
  forwardNoMatch: () => "Ничего не найдено",
  forwardOf: (authors) => `Пересылка · ${authors}`,
  forwardAuthors: (names) =>
    names.length <= 1
      ? (names[0] ?? "")
      : names.length === 2
        ? `${names[0]} и ${names[1]}`
        : `${names[0]}, ${names[1]} и ещё ${names.length - 2}`,
  forwardCount: (n) => `${n} ${plural("ru", n, "сообщение", "сообщения", "сообщений")}`,
  selectedCount: (n) => `Выбрано: ${n}`,
  forwardUnsupported: () => "Этот сервер пока не умеет пересылать сообщения.",
  selectionCopy: () => "Копировать",
  selectionCopied: () => "Текст скопирован",
  codeCopied: () => "Скопировано",
  tagNotFound: () => "Не найдено",
  copyFailed: () => "Текст не скопировался",
  deleteMessagesTitle: (n) =>
    n === 1
      ? "Удалить сообщение?"
      : `Удалить ${n} ${plural("ru", n, "сообщение", "сообщения", "сообщений")}?`,
  deleteMessagesBody: (n, everybody) => {
    if (everybody) {
      return n === 1
        ? "«У всех» — оно исчезнет у каждого в этом чате. «У себя» — только у вас, остальные его сохранят."
        : "«У всех» — они исчезнут у каждого в этом чате. «У себя» — только у вас, остальные их сохранят.";
    }
    return n === 1
      ? "Оно исчезнет только у вас — у остальных в чате оно останется."
      : "Они исчезнут только у вас — у остальных в чате они останутся.";
  },
  deleteMessagesBodyEverybody: (n) =>
    n === 1
      ? "Оно исчезнет у каждого в этом чате. Комментарий старше четырёх часов удаляется только у всех."
      : "Они исчезнут у каждого в этом чате. Комментарии старше четырёх часов удаляются только у всех.",
  deleteManyUnsupported: () => "Этот сервер пока не умеет удалять несколько сообщений сразу.",
  deleteForMeUnsupported: () => "Этот сервер пока не умеет удалять сообщения только у себя.",
  deleteChatUnsupported: () => "Этот сервер пока не умеет удалять чат только у вас.",

  svcCallStarted: (who) => `${who} начинает звонок`,
  svcCallLive: (n) => `Идёт звонок · ${n} ${plural("ru", n, "участник", "участника", "участников")}`,
  svcCallEnded: (d) => (d ? `Звонок завершён · ${d}` : "Звонок завершён"),
  svcJoined: (who) => `${who} теперь в беседе`,
  svcJoinedBy: (by, who) => `${by} добавляет ${who} в беседу`,
  svcLeft: (who) => `${who} покидает беседу`,
  svcRemoved: (who, whom) => `${who} исключает ${whom}`,
  svcAdminGranted: (who, whom) => `${who} назначает ${whom} администратором`,
  svcAdminRevoked: (who, whom) => `${who} снимает с ${whom} права администратора`,
  svcRenamed: (who, name) => `${who} переименовывает беседу в «${name}»`,
  svcBanned: (who, whom, until) =>
    until ? `${who} блокирует ${whom} до ${until}` : `${who} блокирует ${whom}`,
  svcUnbanned: (who, whom) => `${who} снимает блокировку с ${whom}`,
  svcReturned: (who, admin) =>
    admin ? `${who} снова в беседе, с правами администратора` : `${who} снова в беседе`,
  svcVoiceMuted: (who, whom, until) =>
    until
      ? `${who} выключает микрофон ${whom} до ${until}`
      : `${who} выключает микрофон ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} возвращает ${whom} микрофон`,
  svcOwnerChanged: (who, whom) => `${who} передаёт беседу ${whom}`,
  svcUnknown: () => "Обновите EchoFloat, чтобы увидеть событие",
  svcPreview: (kind) => {
    switch (kind) {
      case "joined": return "Новый участник";
      case "left": return "Участник вышел";
      case "removed": return "Участник исключён";
      case "admin_granted": return "Новый администратор";
      case "admin_revoked": return "Администратор снят";
      case "renamed": return "Беседа переименована";
      case "banned": return "Блокировка";
      case "unbanned": return "Блокировка снята";
      case "returned": return "Участник вернулся";
      case "voice_muted": return "Микрофон выключен";
      case "voice_unmuted": return "Микрофон включён";
      case "owner_changed": return "Новый владелец";
      default: return "Событие";
    }
  },
  previewCallLive: () => "Идёт звонок",
  previewCallLasted: (d) => `Звонок — ${d}`,
  previewCallUnanswered: () => "Звонок без ответа",
  previewCallInterrupted: () => "Звонок прерван",
  previewYou: () => "Вы",

  desktopOfferTitle: () => "Открыть в приложении EchoFloat?",
  desktopOpen: () => "Открыть в EchoFloat",
  desktopOpening: () => "Открываем EchoFloat…",
  desktopNoApp: () => "Нет приложения?",
  desktopInstallTitle: () => "EchoFloat для Windows: звонки и чаты в своём окне",
  desktopMissingTitle: () => "Похоже, EchoFloat не установлен",
  desktopStore: () => "Установить из Microsoft Store",
  desktopInstaller: () => "Скачать установщик",
  desktopHaveIt: () => "Уже установлен — открыть",
  desktopDismiss: () => "Остаться в браузере",

  tabBroadcasts: () => "Каналы",
  broadcastKind: () => "Канал",
  broadcastPrivateKind: () => "Приватный канал",
  broadcastOpen: () => "Открыть канал",
  broadcastSubscribers: (n) =>
    `${n} ${plural("ru", n, "подписчик", "подписчика", "подписчиков")}`,
  broadcastSubtitle: (n) =>
    `Канал · ${n} ${plural("ru", n, "подписчик", "подписчика", "подписчиков")}`,
  broadcastSubscribing: () => "Подписываемся…",
  broadcastSubscribe: () => "Подписаться",
  broadcastUnsubscribe: () => "Отписаться",
  broadcastSubscribeHint: () => "Подпишитесь, чтобы канал был в списке",
  broadcastName: () => "Название канала",
  broadcastPublic: () => "Публичный",
  broadcastPublicHint: () => "Его можно найти и читать без приглашения",
  broadcastPrivate: () => "Приватный",
  broadcastPrivateHint: () => "Только по приглашению",
  broadcastHandle: () => "Публичное имя",
  broadcastHandleHint: (link) => `По нему канал находят и открывают: ${link}`,
  broadcastHandleRule: () => "От 3 до 64 латинских букв и цифр, через _ или -, первая — буква",
  broadcastCreate: () => "Создать канал",
  broadcastNotFound: () => "Канал не найден",
  broadcastsNotFound: () => "Каналов не найдено",
  broadcastUnsupported: () => "Этот сервер пока не умеет создавать каналы.",
  broadcastSettingsDesktop: () => "Эти настройки — в приложении для компьютера",
  verifiedTip: () => "Официальный канал",
  broadcastPostPlaceholder: () => "Пост от имени канала…",
  postNoComments: () => "Без комментариев",
  postNoReactions: () => "Без реакций",
  postTooLong: (limit) => `Пост длиннее ${limit} знаков — сократите его`,
  sendAsChannel: () => "От имени канала",
  sendAsSelf: () => "От себя",
  commentPlaceholder: () => "Комментарий",
  viewsCount: (n) => `${n} ${plural("ru", n, "просмотр", "просмотра", "просмотров")}`,
  viewsShort: (n) => viewsShort(n, ","),
  commentsCount: (n) =>
    `${n} ${plural("ru", n, "комментарий", "комментария", "комментариев")}`,
  commentsLeave: () => "Прокомментировать",
  commentsTitle: () => "Комментарии",
  commentsOff: () => "Комментарии к этому посту выключены",
  commentsSubscribe: () => "Подпишитесь, чтобы комментировать",
  commentsRestricted: (until) => `Вы не можете комментировать до ${until}`,
  commentsRestrictedForever: () => "Вы не можете комментировать в этом канале",
  commentsEmpty: () => "Комментариев пока нет",
  commentReplyToast: (channel) => `${channel}: ответ на ваш комментарий`,
  channelDeleteRefused: () =>
    "В канале сообщения удаляются только у всех, а некоторые из выбранных вам удалять нельзя.",
  commentKindRefused: () => "В комментариях этого канала такое отправлять нельзя.",
  postCopyLink: () => "Копировать ссылку",
  postLinkCopied: () => "Ссылка скопирована",
  menuCommentsOff: () => "Выключить комментарии",
  menuCommentsOn: () => "Включить комментарии",
  menuReactionsOff: () => "Выключить реакции",
  menuReactionsOn: () => "Включить реакции",
  postDetailsPosted: (name) => `Опубликовал(а) ${name}`,
  postDetailsEdited: (name) => `Изменил(а) ${name}`,
  forwardedPrivateChannel: () => "Переслано из приватного канала",
  unsupportedChat: () => "Обновите EchoFloat, чтобы открыть этот чат.",

  officialMark: () => "Официальный аккаунт EchoFloat",
  officialOwner: () => "Официальный аккаунт сервера",
  officialBar: () => "Это служебный чат — отвечать сюда не нужно.",
  officialOffer: () => "Если что-то в приложении беспокоит, расскажите нам.",
  officialEmpty: () =>
    "Здесь EchoFloat пишет о новостях сервера и сообщает о входах в ваш аккаунт с других устройств.",
  noticeSignin: () => "Вход в ваш аккаунт",
  noticeRow: (device) => `Новый вход: ${isolated(device)}`,
  noticeToast: (device) => `Вход в ваш аккаунт: ${isolated(device)}. Это вы?`,
  noticeMine: () => "Это я",
  noticeEnd: () => "Завершить сеанс",
  noticeEnded: () => "Сеанс завершён. Если это были не вы, смените пароль.",
  noticePassword: () => "Сменить пароль",
  noticeUnknown: () => "Уведомление безопасности — обновите EchoFloat, чтобы прочитать его.",
  noticeJustNow: () => "только что",
  sessionsUnknownDevice: () => "Неизвестное устройство",
  sessionsEndConfirm: (device) =>
    `${isolated(device)} выйдет из аккаунта. Остальные сеансы останутся.`,
  sessionsEndDo: () => "Завершить",
  feedback: () => "Отправить отзыв",
  feedbackBody: () =>
    "Расскажите, что случилось — это не обязательно. Вместе с отзывом уйдёт короткий отчёт: версия приложения, браузер и система.",
  feedbackPlaceholder: () => "Что случилось?",
  feedbackSent: () => "Отзыв отправлен. Спасибо.",
  floatsCode: (amount, until) =>
    until === null
      ? `Вам промокод на ${floats(amount, "ru")}`
      : `Вам промокод на ${floats(amount, "ru")}, действует до ${until}`,

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s} с`;
    return s === 0 ? `${m} мин` : `${m} мин ${s} с`;
  },
};

const en: Dict = {
  ...tipsIn("en"),
  loginTitle: () => "EchoFloat",
  loginUsername: () => "Username",
  loginPassword: () => "Password",
  loginEmail: () => "Email",
  loginSubmit: () => "Sign in",
  registerSubmit: () => "Create account",
  loginSwitchToRegister: () => "No account? Register",
  loginSwitchToLogin: () => "Have an account? Sign in",
  loginAgeGate: () => "I am 13 or older and accept the terms",
  loginFailed: (reason) => reason,
  loginForgot: () => "Forgot password?",

  codeTitle: () => "Confirm your e-mail",
  codeSent: () => "We sent a code to the address you entered",
  codeField: () => "Code from the letter",
  codeSubmit: () => "Confirm",
  codeResend: () => "Send it again",
  codeResendIn: (seconds) => `You can ask again in ${seconds} s`,
  codeExpiresIn: (seconds) => `The code is good for another ${clock(seconds)}`,
  codeExpired: () => "The code has expired — ask for a new one",
  codeBad: () => "The code is wrong or has expired",
  codeBurnt: () => "Too many wrong codes. Start again.",
  codeBack: () => "Back",
  recoverTitle: () => "Password recovery",
  recoverLead: () => "Enter your e-mail or username — the code goes to the account's e-mail.",
  recoverLogin: () => "E-mail or username",
  recoverCodeSent: () => "We sent a code to the account's e-mail",
  recoverSubmit: () => "Send the code",
  recoverNewPassword: () => "New password",
  recoverRepeatPassword: () => "Repeat the password",
  recoverSave: () => "Save the password",
  recoverDone: () => "Password changed — sign in with the new one.",
  passwordsDiffer: () => "The passwords do not match",
  mailUnavailable: () => "This server cannot send e-mail.",
  errorNotLoggedIn: () => "You are not signed in",
  errorTimeout: () => "The server did not answer in time",
  errorNetwork: () => "Cannot reach the server. Check your connection.",
  errorHttp: (status) =>
    status >= 500
      ? `The server could not handle the request (HTTP ${status}). Try again later.`
      : `The server refused the request (HTTP ${status})`,
  errorBadAnswer: () => "The server's answer made no sense. Reload the page.",
  addressLead: () =>
    "To continue, confirm your e-mail: enter the account's password and the address — we will send a six-digit code to it.",
  addressSubmit: () => "Send code",
  addressSignOut: () => "Sign out",
  profileOpen: () => "Open profile",
  profileChangePhoto: () => "Change photo",
  profileDisplayName: () => "Name in the app",
  profileBio: () => "Bio",
  profileChangePassword: () => "Change password",
  profileChangeEmail: () => "Change e-mail",
  profileAnalytics: () => "Send usage statistics",
  profileAnalyticsHint: () =>
    "When it's off, the server keeps no day-by-day records about your account and deletes the ones it has. Totals not tied to anyone, like the number of messages sent in a day, are kept either way.",
  profileNotices: () => "Third-party notices",

  emailTitle: () => "Change e-mail",
  emailCurrent: () => "Current e-mail",
  emailNone: () => "No e-mail on record",
  emailCurrentLead: () =>
    "First confirm the current address is yours: we will send a code to it.",
  emailRequestCode: () => "Request code",
  emailRequestCodeIn: (seconds) => `You can request a code in ${seconds} s`,
  emailSentCurrent: () => "We sent a code to your current address",
  emailNext: () => "Next",
  emailNew: () => "New e-mail",
  emailNewLead: () => "Now enter the new address — the code goes to it.",
  emailSentNew: () => "We sent a code to the new address",
  emailSave: () => "Change e-mail",
  emailChanged: () => "E-mail changed",
  emailTaken: () => "This address is already in use",
  emailBad: () => "That does not look like an e-mail address",
  emailTooMany: () => "Too many requests — try again later",
  emailChangeExpired: () => "The e-mail change has expired — start again",
  emailChangeUnsupported: () => "This server cannot change e-mail yet — it needs an update.",

  channels: () => "Groups",
  allTab: () => "All",
  friendsTab: () => "Friends",
  searchPlaceholder: () => "Search",
  newChannel: () => "New channel",
  newGroup: () => "New group",
  groupUnsupported: () => "This server can't create groups yet.",
  petsTab: () => "Pets",
  newPet: () => "New pet",
  petsEmpty: () => "No pets yet. They show up here once someone makes one on the server.",
  petsMore: () => "More pets",
  petsNotFound: () => "No such pets",
  petMark: () => "Pet",
  friendMark: () => "Friend",
  petsUnsupported: () => "This server can't work with pets yet.",
  members: (n) => `${n} ${plural("en", n, "member", "members", "members")}`,
  connecting: () => "Connecting…",
  reconnecting: () => "Reconnecting…",
  offline: () => "No connection",

  today: () => "Today",
  yesterday: () => "Yesterday",
  unreadSeparator: () => "Unread",
  edited: () => "edited",
  unsupportedMessage: () => "Update EchoFloat to open this message",
  unsupportedFormatting: () => "Some formatting isn't shown",
  unsupportedAttachment: () => "Update EchoFloat to open this attachment",
  stickerUnknownHint: () => "Update EchoFloat.",
  mediaPreview: (kind, count, durationMs) => {
    const many = count > 1 ? `${count} ` : "";
    switch (kind) {
      case "photo":
        return `📷 ${many}${count > 1 ? "photos" : "photo"}`;
      case "video":
        return `🎬 ${many}${count > 1 ? "videos" : "video"}`;
      case "media":
        return `🖼 ${many}media`;
      case "video_message":
        return "⭕ video message";
      case "voice":
        return durationMs != null ? `🎤 ${clock(durationMs / 1000)}` : "🎤 voice message";
    }
  },
  loadOlder: () => "Earlier",
  emptyChannel: () => "Start the conversation",
  albumPosition: (index, total) => `${index} of ${total}`,
  albumReveal: () => "Show",
  viewerPhoto: () => "Photo",

  composerPlaceholder: () => "Message",
  mentionHeading: () => "Members",
  commandHeading: () => "Commands",
  petRestart: () => "Restart",
  petRetired: () => "This pet no longer works",
  dmFormerTitle: () => "You're no longer friends",
  dmFormerHint: () => "This chat is read-only",
  dmForget: () => "Delete chat",
  dmForgetBody: () =>
    "Delete the chat? It leaves your list. If they've deleted it already, the history is gone for good.",
  dmClearBody: () =>
    "The whole conversation disappears for you only — the other side keeps it. You can keep writing to each other as before.",
  composerJoinFirst: () => "Join to write",
  joinChannel: () => "Join",
  slowModeWait: (s) => `Wait ${s}s`,
  messageTooLongSplit: (n) => `Message will be sent in ${n} parts`,
  send: () => "Send",
  recordingCancelled: () => "Recording cancelled",
  attach: () => "Attach",
  attachMedia: () => "Photo or video",
  attachFile: () => "File",
  viewAttachment: () => "View",
  originalOverLimit: (b) =>
    `A picture can go as a file up to ${
      Math.floor(b / (1024 * 1024))
    } MiB — this one is larger, so attach it as a photo`,
  uploadPhotos: (n) => (n === 1 ? "Sending a photo" : `Sending ${n} photos`),
  uploadVideos: (n) => (n === 1 ? "Sending a video" : `Sending ${n} videos`),
  uploadFiles: (n) => (n === 1 ? "Sending a file" : `Sending ${n} files`),
  uploadMedia: () => "Sending media",
  uploadAmount: (done, total) => {
    const [a, b, unit] = amountIn("en", done, total);
    return `${a} of ${b} ${unit}`;
  },
  uploadCancel: () => "Cancel sending",
  typing: (dialog, names) =>
    dialog
      ? "typing"
      : names.length === 1
        ? `${names[0]} is typing`
        : names.length === 2
          ? `${names[0]} and ${names[1]} are typing`
          : "Several people are typing",

  ttlLong: (n, unit) => {
    switch (unit) {
      case "d": return `${n} ${plural("en", n, "day", "days", "days")}`;
      case "h": return `${n} ${plural("en", n, "hour", "hours", "hours")}`;
      case "m": return `${n} ${plural("en", n, "minute", "minutes", "minutes")}`;
      default: return `${n} ${plural("en", n, "second", "seconds", "seconds")}`;
    }
  },
  ttlAfter: (n, unit) => en.ttlLong(n, unit),
  ttlShort: (n, unit) => `${n}${unit}`,
  ttlHoursLeft: (h) => `${h}h`,
  ttlClockOn: (lifetime) => `Messages are deleted ${lifetime} after they are read`,
  ttlPlaceholder: (lifetime) => `Deleted ${lifetime} after it is read`,
  ttlAfterRead: (lifetime) => `Deleted ${lifetime} after it is read`,
  ttlAutoDelete: () => "Auto-deleted message",
  ttlTurnOff: () => "No auto-delete",
  ttlDisappearsIn: (left) => `Deleted in ${left}`,
  ttlUnsupported: () =>
    "This server does not support auto-deleted messages yet — this one will stay",

  cardAdd: () => "Add friend",
  cardPending: () => "Request sent",
  cardCancelRequest: () => "Cancel request",
  cardAccept: () => "Accept",
  cardDecline: () => "Decline",
  cardMessage: () => "Message",
  cardUnfriend: () => "Unfriend",
  cardBlock: () => "Block",
  cardUnblock: () => "Unblock",
  cardPolicyClosed: () => "Requests are closed",
  requestsTitle: () => "Friend requests",
  toastRequest: (who) => `${who}: friend request`,
  toastAccepted: (who) => `${who}: request accepted`,
  copyLink: () => "Copy link",
  cardMore: () => "More",

  petStop: () => "Stop",
  petStoppedNote: () => "You stopped this pet: it won't write to you until you restart it.",
  petAddToGroup: () => "Add to group",
  petNoGroups: () => "No groups where you're an admin",
  petAdded: (group) => `Added to “${group}”`,
  petOwner: (name) => `Owner: ${name}`,
  petToken: () => "Token",
  petRetire: () => "Retire",
  petRetireWarning: () =>
    "Its token stops working, it leaves every group and answers nobody again. Its name stays taken.",
  petEditTitle: (name) => `Pet “${name}”`,
  petDisplayName: () => "Name",
  petDisplayNameHint: () => "How chats show the pet. Left empty, they show its nickname",
  petNickname: () => "Nickname",
  petDescription: (tag) => `Description in ${inEnglish(tag)}`,
  petCommandName: () => "command",
  petCommandDescription: (tag) => `What it does, in ${inEnglish(tag)}`,
  petAddCommand: () => "Add command",
  petRemoveCommand: () => "Remove command",
  petMoveUp: () => "Move up",
  petMoveDown: () => "Move down",
  petJoinable: () => "Can be added to groups",
  petPhotoTooLarge: (mib) => `A photo may be at most ${mib} MiB`,
  petPhotoNotImage: () => "That file is not a webp, jpeg, png or avif",
  petTokenTitle: () => "Pet token",
  petTokenOnce: () =>
    "Copy the token now: it won't be shown again. The pet's program signs in to EchoFloat with it.",
  petTokenLost: () =>
    "A token is shown only once. If it's lost, issue a new one — the old one stops working at once.",
  petTokenRotate: () => "Issue a new one",
  petTokenCopied: () => "Token copied",

  linkKindChannel: () => "Group",
  linkKindGroup: () => "Group",
  linkKindDm: () => "Direct messages",
  linkKindUser: () => "Profile",
  linkOpenChannel: () => "Open group",
  linkOpenGroup: () => "Open group",
  linkOpenChat: () => "Open chat",
  linkAddFriend: () => "Add friend",
  linkAcceptRequest: () => "Accept request",
  linkPreview: () => "Link preview",
  linkPreviewDismiss: () => "Send without preview",
  loading: () => "Loading…",

  ringIncoming: (who, channel) => `${who} is calling in “${channel}”`,
  ringJoinHint: () => "Answer",
  ringDecline: () => "Decline",
  callPill: (n) => `Call · ${n}`,
  inCallN: (n) => `${n} in the call`,

  presenceOnline: () => "Online",
  presenceAfk: () => "AFK",
  presenceDnd: () => "Do not disturb",
  presenceInvisible: () => "Invisible",
  presenceInCall: () => "In a call",
  presenceInCallAt: (channel) => `In a call in “${channel}”`,

  voiceJoin: () => "Join",
  voiceConnecting: () => "Connecting…",
  voiceKeyExchange: () => "Securing…",
  voiceLiveRtt: (ms) => `Microphone on · ${ms} ms`,
  voiceMutedState: () => "Microphone off",
  voiceDeafenedState: () => "Sound is off",
  voicePoorLoss: (pct) => `Poor connection · ${pct.toFixed(0)}% loss`,
  voicePoorRtt: (ms) => `Poor connection · ${ms} ms`,
  voiceMute: () => "Mute",
  voiceUnmute: () => "Unmute",
  voiceDeafen: () => "Mute sound",
  voiceUndeafen: () => "Unmute sound",
  voiceDisconnect: () => "Leave",
  voiceE2eeNote: () => "The call is end-to-end encrypted. Chat isn't",
  voiceUnlisted: () => "Someone not on the list holds this call's keys. You can't be heard",
  voiceMicDenied: () => "Allow microphone access",
  voiceUnsupported: () => "Update Chrome to make calls",
  voiceDropped: () => "Call ended unexpectedly",
  callRatingTitle: () => "How was the call?",
  callRatingBody: () =>
    "Rate the connection. The rating is kept together with this call's connection figures — delay and loss.",
  callRatingBad: () => "Bad",
  callRatingGood: () => "Good",
  callRatingClose: () => "Close",
  callRatingThanks: () => "Thanks for the rating!",

  watchStream: () => "Watch stream",
  callRoomOpen: () => "Open call room",
  callRoomClose: () => "Back to chat",
  stopWatching: () => "Close stream",
  streamOf: (name) => `${name}'s stream`,
  streamPaused: () => "Stream paused",
  streamCapNote: () => "Up to 720p 60 fps in the browser. Full quality in the EchoFloat app",
  shareVolume: () => "Stream volume",
  streamAudienceOpen: () => "Who is watching",
  streamAudienceNobody: () => "Nobody is watching yet",
  streamAudienceHeading: (n) => `Viewers — ${n}`,
  streamEndedStopped: () => "Stream ended",
  streamEndedSourceGone: () => "Window closed",
  streamEndedLeft: () => "Streamer left the call",
  streamEndedAdmin: () => "Admin stopped the stream",

  cameraOnMark: () => "Camera on — visible in the EchoFloat app for Windows",

  consoleTitle: (name) => `“${name}” settings`,
  consoleDone: () => "Done",
  consoleCancel: () => "Cancel",
  consoleAccess: () => "Access",
  consolePrivate: () => "Private group",
  groupPrivate: () => "Private group",
  groupPrivateHint: () => "Only its members can see a group, and it can't be made public.",
  consoleLimits: () => "Limits",
  consoleVoiceLimit: (s) => `Voice message up to ${s}s`,
  consoleMediaLimit: (mb) => `Files up to ${mb} MB`,
  consoleCharsLimit: (n) => `Message up to ${n} characters`,
  consoleSlowmode: (s) => (s === 0 ? "Slow mode: off" : `Slow mode: ${s} s`),
  consoleVoiceEnabled: () => "Calls",
  consoleInvite: () => "Invite",
  consoleInviteNone: () => "No link",
  consoleInviteCreate: () => "Create",
  consoleInviteCopy: () => "Copy",
  consoleInviteCopied: () => "Link copied",
  consoleInviteRefresh: () => "New link",
  consoleInviteRevoke: () => "Delete",
  consoleInviteTtl: () => "The link lasts 3 days. A new link replaces it",
  consoleAppearance: () => "Appearance",
  consoleBgLight: () => "Background in light theme",
  consoleBgDark: () => "Background in dark theme",
  consoleBgPick: () => "Pick",
  consoleBgRemove: () => "Remove",
  consoleBgSet: () => "set",
  consoleBgNone: () => "none",
  consoleBgWillRemove: () => "will be removed",
  consoleBgBlur: (n) => `Background blur: ${n}`,
  consoleBgBrightness: (n) => `Background brightness: ${n}`,
  consolePeople: () => "People",
  consoleRoleOwner: () => "owner",
  consoleRoleAdmin: () => "admin",
  consoleMakeAdmin: () => "Make admin",
  consoleRevokeAdmin: () => "Remove admin",
  consoleMakeOwner: () => "Transfer group",
  consoleVoiceMute: () => "Mute",
  consoleVoiceUnmute: () => "Unmute",
  consoleKick: () => "Remove",
  consoleBanDay: () => "Ban for a day",
  consoleBanForever: () => "Ban forever",
  consoleBanned: () => "Banned",
  consoleBanUntil: (when) => `until ${when}`,
  consoleBanPermanent: () => "forever",
  consoleUnban: () => "Unban",

  menuReport: () => "Report",
  reportTitle: () => "Report",
  reportReasonLabel: (key) => {
    switch (key) {
      case "abuse": return "Abuse";
      case "sexual": return "Sexual content";
      case "child": return "Child endangerment";
      case "violence": return "Violence";
      case "illegal": return "Illegal";
      case "spam": return "Spam";
      default: return "Other";
    }
  },
  reportCommentPlaceholder: () => "Comment (optional)",
  reportSend: () => "Send",
  reportSent: () => "Report sent",
  reportsTitle: () => "Reports",
  reportsOpen: (n) => `Open: ${n}`,
  reportsShowResolved: () => "Show closed",
  reportsRemove: () => "Removed",
  reportsDismiss: () => "Dismiss",
  reportsEmpty: () => "No reports",

  stickersTab: () => "Stickers",
  stickersRecent: () => "Recent",
  stickersEmpty: () => "Your stickers will be here. Find packs in the catalogue.",
  stickersCatalog: () => "Sticker catalogue",
  stickersSearch: () => "Search packs",
  stickersPopular: () => "Popular",
  stickersNew: () => "New",
  stickersNoneFound: () => "Nothing found",
  stickersLoadFailed: () => "Could not load",
  stickersRetry: () => "Try again",
  stickersAdd: () => "Add",
  stickersRemove: () => "Remove",
  stickersPackKind: () => "Sticker pack",
  stickersPackGone: () => "This pack is not available",
  stickerRemoved: () => "Sticker removed",
  stickerBarLabel: (emoji) => `Stickers for ${emoji}`,
  stickerBarHide: () => "Don't suggest stickers",
  stickersReportBody: () => "An administrator will review the pack. What is wrong?",
  stickersTakeDown: () => "Take the pack down",
  stickersTakeDownBody: () =>
    "The pack leaves the catalogue and everyone's panel. Stickers already sent show as removed.",
  stickersBlockFiles: () => "Take down and block its files",
  stickersBlockFilesBody: () =>
    "For unlawful content only: the files are deleted from the server and stop loading in every chat.",
  stickersMyPacks: () => "My packs",
  stickersTitle: () => "Pack title",
  stickersTitleHint: () => "For example, “Cats”",
  stickersCreate: () => "Create",
  stickersDraft: () => "Draft",
  stickersPublished: () => "Published",
  stickersTakenDown: () => "Taken down by an administrator",
  stickersAddSticker: () => "Add a sticker",
  stickersFileHint: () =>
    "TGS, Lottie JSON or .lottie up to 3 seconds — or a PNG, WebP or JPEG picture",
  stickersEmoji: () => "The sticker's emoji",
  stickersEmojiHint: () => "One to eight — what the sticker is found by",
  stickersFileUnfit: () => "This file does not fit",
  stickersEmptyPack: () => "No stickers in the pack yet",
  stickersDraftHint: () => "A draft only you can see. Publish the pack to send its stickers.",
  stickersPublish: () => "Publish",
  stickersPublishTitle: () => "Publish the pack?",
  stickersPublishBody: () =>
    "Everyone will see the pack. Publish only art that is yours or that you have the rights to: packs from Telegram belong to their authors.",
  stickersRules: () => "Publishing rules",
  stickersDeletePack: () => "Delete the pack",
  stickersDeletePackBody: () =>
    "The pack leaves the catalogue and everyone's panel. Stickers already sent stay in the chats.",
  stickersViewPack: () => "View the pack",
  linkOpenPack: () => "Open the pack",
  stickersEdit: () => "Edit",
  stickersSave: () => "Save",
  stickersCancel: () => "Cancel",
  stickersClose: () => "Close",
  stickersBack: () => "Back",
  stickersDelete: () => "Delete",
  stickersAnimated: () => "Animated stickers",
  stickersAnimatedHint: () => "Off, stickers rest on their first frame.",
  keytips: () => "Key hints on Ctrl",
  keytipsHint: () => "Hold Ctrl to see the key of every control.",
  keys: () => "Keyboard",
  keysNav: () => "Navigation",
  keysChat: () => "Chat",
  keysCall: () => "Call",
  keysTabNext: () => "Next tab",
  keysTabPrev: () => "Previous tab",
  keysTabNumber: () => "Tab by number",
  keysChatNext: () => "Next chat",
  keysChatNextHint: () => "In a field with text, Ctrl+A selects it.",
  keysChatPrev: () => "Previous chat",
  keysChatNumber: () => "Chat by number",
  keysOnRelease: () => "Opens when you let go of Ctrl.",
  keysNumber: () => "#",
  keysSettings: () => "Profile and settings",
  keysNewLine: () => "New line",
  keysToFeed: () => "To the messages",
  keysToFeedHint: () => "From the start of the box.",
  keysWalk: () => "Through the messages",
  keysInside: () => "Into a message",
  keysInsideHint: () => "Its links and reactions, the author last: Tab and the arrows, Esc to go back.",
  keysEditOwn: () => "Edit your message",
  keysCloseHint: () => "A menu, a card, then the open chat.",
  keysCallToggle: () => "Call, answer or hang up",
  keysMute: () => "Microphone",
  keysDeafen: () => "Sound",
  keysTalk: () => "Talk while held",
  keysBrowserKeeps: () => "In a browser tab, Ctrl+T and Ctrl+N belong to the browser; the installed app gets them.",
  keysMenus: () => "Menus and panels",
  keysMenuRows: () => "Through a menu",
  keysFirstLast: () => "First and last item",
  keysSubmenu: () => "Open or close a submenu",
  keysPressRow: () => "Press the item",
  keysPanels: () => "Through stickers and reactions",
  keysPanelsHint: () => "Enter picks; ↑ from the top row goes to the packs' tabs.",
  keysFocusMenu: () => "Menu of what has the focus",
  keysFocusMenuHint: () => "A message, a chat in the list; Space presses the thing itself.",
  keysMessageMenu: () => "Message menu",
  keysReply: () => "Reply to the message",
  keysForward: () => "Forward the message",
  keysPick: () => "Select the message",
  keysCopyMessage: () => "Copy the message",
  keysDeleteMessage: () => "Delete the message",
  stickersReports: () => "Sticker pack reports",
  stickerPreview: (emoji) => emoji || "Sticker",
  stickerLabel: (emoji) => `Sticker ${emoji}`.trimEnd(),
  stickerCount: (n) => (n === 1 ? "1 sticker" : `${n} stickers`),
  stickersBy: (name) => `By ${name}`,
  stickerRejected: (english) => english,

  menuReact: () => "React",
  menuReply: () => "Reply",
  menuEdit: () => "Edit",
  menuDelete: () => "Delete",
  menuDeleteForMe: () => "Delete for me",
  menuDeleteForEverybody: () => "Delete for everybody",
  replyElsewhere: () => "An earlier message",
  replyDeleted: () => "Message deleted",
  menuForward: () => "Forward",
  menuSelect: () => "Select",
  menuDetails: () => "Details",
  detailsNobody: () => "Nobody has read it yet",
  detailsFailed: () => "Could not load",
  readToday: () => "today",
  readYesterday: () => "yesterday",
  readAt: (day, clock) => `${day} at ${clock}`,
  menuRead: () => "Read",
  menuReadAt: (clock) => `Read at ${clock}`,
  menuReadOn: (when) => `Read ${when}`,
  menuEditedAt: (clock) => `Edited at ${clock}`,
  menuEditedOn: (when) => `Edited ${when}`,
  forwardedFrom: () => "Forwarded from",
  forwardedUnknown: () => "Forwarded message",
  forwardTo: () => "Forward to…",
  forwardNowhere: () => "There is nowhere to forward to yet",
  forwardNoMatch: () => "Nothing found",
  forwardOf: (authors) => `Forward · ${authors}`,
  forwardAuthors: (names) =>
    names.length <= 1
      ? (names[0] ?? "")
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names[0]}, ${names[1]} and ${names.length - 2} more`,
  forwardCount: (n) => `${n} ${plural("en", n, "message", "messages", "messages")}`,
  selectedCount: (n) => `${n} selected`,
  forwardUnsupported: () => "This server can't forward messages yet.",
  selectionCopy: () => "Copy",
  selectionCopied: () => "Text copied",
  codeCopied: () => "Copied",
  tagNotFound: () => "Not found",
  copyFailed: () => "The text was not copied",
  deleteMessagesTitle: (n) =>
    n === 1 ? "Delete this message?" : `Delete ${n} ${plural("en", n, "message", "messages", "messages")}?`,
  deleteMessagesBody: (n, everybody) => {
    if (everybody) {
      return n === 1
        ? "For everybody: it disappears for everyone in this chat. For me: only you stop seeing it."
        : "For everybody: they disappear for everyone in this chat. For me: only you stop seeing them.";
    }
    return n === 1
      ? "It will disappear only for you — everyone else in the chat keeps it."
      : "They will disappear only for you — everyone else in the chat keeps them.";
  },
  deleteMessagesBodyEverybody: (n) =>
    n === 1
      ? "It disappears for everyone in this chat. A comment older than four hours can only be deleted for everybody."
      : "They disappear for everyone in this chat. Comments older than four hours can only be deleted for everybody.",
  deleteManyUnsupported: () => "This server can't delete several messages at once yet.",
  deleteForMeUnsupported: () => "This server can't delete messages for you only yet.",
  deleteChatUnsupported: () => "This server can't delete a chat for you only yet.",

  svcCallStarted: (who) => `${who} starts a call`,
  svcCallLive: (n) => `Call · ${n} ${plural("en", n, "participant", "participants", "participants")}`,
  svcCallEnded: (d) => (d ? `Call ended · ${d}` : "Call ended"),
  svcJoined: (who) => `${who} joined the group`,
  svcJoinedBy: (by, who) => `${by} added ${who} to the group`,
  svcLeft: (who) => `${who} leaves the group`,
  svcRemoved: (who, whom) => `${who} removes ${whom}`,
  svcAdminGranted: (who, whom) => `${who} makes ${whom} an admin`,
  svcAdminRevoked: (who, whom) => `${who} removes ${whom} as admin`,
  svcRenamed: (who, name) => `${who} renames the group to “${name}”`,
  svcBanned: (who, whom, until) =>
    until ? `${who} bans ${whom} until ${until}` : `${who} bans ${whom}`,
  svcUnbanned: (who, whom) => `${who} lifts ${whom}'s ban`,
  svcReturned: (who, admin) =>
    admin ? `${who} is back in the group, with admin rights` : `${who} is back in the group`,
  svcVoiceMuted: (who, whom, until) =>
    until ? `${who} mutes ${whom} until ${until}` : `${who} mutes ${whom}`,
  svcVoiceUnmuted: (who, whom) => `${who} unmutes ${whom}`,
  svcOwnerChanged: (who, whom) => `${who} hands the group to ${whom}`,
  svcUnknown: () => "Update EchoFloat to see this event",
  svcPreview: (kind) => {
    switch (kind) {
      case "joined": return "New member";
      case "left": return "Member left";
      case "removed": return "Member removed";
      case "admin_granted": return "New admin";
      case "admin_revoked": return "Admin revoked";
      case "renamed": return "Group renamed";
      case "banned": return "Ban";
      case "unbanned": return "Ban lifted";
      case "returned": return "Member returned";
      case "voice_muted": return "Muted";
      case "voice_unmuted": return "Unmuted";
      case "owner_changed": return "New owner";
      default: return "Event";
    }
  },
  previewCallLive: () => "Live call",
  previewCallLasted: (d) => `Call — ${d}`,
  previewCallUnanswered: () => "Nobody answered",
  previewCallInterrupted: () => "Call interrupted",
  previewYou: () => "You",

  desktopOfferTitle: () => "Open in the EchoFloat app?",
  desktopOpen: () => "Open in EchoFloat",
  desktopOpening: () => "Opening EchoFloat…",
  desktopNoApp: () => "No app?",
  desktopInstallTitle: () => "EchoFloat for Windows: calls and chats in a window of their own",
  desktopMissingTitle: () => "EchoFloat doesn't seem to be installed",
  desktopStore: () => "Get it from Microsoft Store",
  desktopInstaller: () => "Download installer",
  desktopHaveIt: () => "Already installed — open",
  desktopDismiss: () => "Stay in the browser",

  tabBroadcasts: () => "Channels",
  broadcastKind: () => "Channel",
  broadcastPrivateKind: () => "Private channel",
  broadcastOpen: () => "Open channel",
  broadcastSubscribers: (n) =>
    `${n} ${plural("en", n, "subscriber", "subscribers", "subscribers")}`,
  broadcastSubtitle: (n) =>
    `Channel · ${n} ${plural("en", n, "subscriber", "subscribers", "subscribers")}`,
  broadcastSubscribing: () => "Subscribing…",
  broadcastSubscribe: () => "Subscribe",
  broadcastUnsubscribe: () => "Unsubscribe",
  broadcastSubscribeHint: () => "Subscribe to keep the channel in your list",
  broadcastName: () => "Channel name",
  broadcastPublic: () => "Public",
  broadcastPublicHint: () => "Anybody can find and read it",
  broadcastPrivate: () => "Private",
  broadcastPrivateHint: () => "By invitation only",
  broadcastHandle: () => "Public handle",
  broadcastHandleHint: (link) => `People find and open the channel by it: ${link}`,
  broadcastHandleRule: () =>
    "3 to 64 Latin letters and digits, joined by _ or -, starting with a letter",
  broadcastCreate: () => "Create channel",
  broadcastNotFound: () => "Channel not found",
  broadcastsNotFound: () => "No channels found",
  broadcastUnsupported: () => "This server cannot make channels yet.",
  broadcastSettingsDesktop: () => "These settings are in the desktop app",
  verifiedTip: () => "Official channel",
  broadcastPostPlaceholder: () => "Post as the channel…",
  postNoComments: () => "No comments",
  postNoReactions: () => "No reactions",
  postTooLong: (limit) => `A post can be at most ${limit} characters — shorten it`,
  sendAsChannel: () => "As the channel",
  sendAsSelf: () => "As yourself",
  commentPlaceholder: () => "Comment",
  viewsCount: (n) => `${n} ${plural("en", n, "view", "views", "views")}`,
  viewsShort: (n) => viewsShort(n, "."),
  commentsCount: (n) => `${n} ${plural("en", n, "comment", "comments", "comments")}`,
  commentsLeave: () => "Leave a comment",
  commentsTitle: () => "Comments",
  commentsOff: () => "Comments are turned off for this post",
  commentsSubscribe: () => "Subscribe to comment",
  commentsRestricted: (until) => `You cannot comment until ${until}`,
  commentsRestrictedForever: () => "You cannot comment in this channel",
  commentsEmpty: () => "No comments yet",
  commentReplyToast: (channel) => `${channel}: a reply to your comment`,
  channelDeleteRefused: () =>
    "In a channel, messages are deleted only for everybody, and some of these aren't yours to delete.",
  commentKindRefused: () => "This channel doesn't take that in comments.",
  postCopyLink: () => "Copy link",
  postLinkCopied: () => "Link copied",
  menuCommentsOff: () => "Turn comments off",
  menuCommentsOn: () => "Turn comments on",
  menuReactionsOff: () => "Turn reactions off",
  menuReactionsOn: () => "Turn reactions on",
  postDetailsPosted: (name) => `Published by ${name}`,
  postDetailsEdited: (name) => `Edited by ${name}`,
  forwardedPrivateChannel: () => "Forwarded from a private channel",
  unsupportedChat: () => "Update EchoFloat to open this chat.",

  officialMark: () => "Official EchoFloat account",
  officialOwner: () => "The server's official account",
  officialBar: () => "This is a service chat — no need to reply here.",
  officialOffer: () => "If something in the app bothers you, tell us.",
  officialEmpty: () =>
    "EchoFloat writes here about server news and tells you when someone signs in to your account on another device.",
  noticeSignin: () => "Someone signed in to your account",
  noticeRow: (device) => `New sign-in: ${isolated(device)}`,
  noticeToast: (device) => `Someone signed in to your account: ${isolated(device)}. Was it you?`,
  noticeMine: () => "It was me",
  noticeEnd: () => "End session",
  noticeEnded: () => "Session ended. If it wasn't you, change your password.",
  noticePassword: () => "Change password",
  noticeUnknown: () => "A security notice — update EchoFloat to read it.",
  noticeJustNow: () => "just now",
  sessionsUnknownDevice: () => "Unknown device",
  sessionsEndConfirm: (device) =>
    `${isolated(device)} will be signed out of this account. Other sessions stay.`,
  sessionsEndDo: () => "End",
  feedback: () => "Send feedback",
  feedbackBody: () =>
    "Tell us what happened — that part is optional. A short report goes with it: the app's version, the browser and the system.",
  feedbackPlaceholder: () => "What happened?",
  feedbackSent: () => "Feedback sent. Thank you.",
  floatsCode: (amount, until) =>
    until === null
      ? `You have a promo code for ${floats(amount, "en")}`
      : `You have a promo code for ${floats(amount, "en")}, valid until ${until}`,

  durationS: (total) => {
    const m = Math.floor(total / 60);
    const s = total % 60;
    if (m === 0) return `${s}s`;
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
  },
};

export function dict(lang: Lang): Dict {
  return lang === "ru" ? ru : en;
}
